import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixStoresCatalogV3CatalogImport from './stores-catalog-v3-catalog-import-catalog-imports-v-3.http.js';
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformSDKVideoV2ToRESTVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

/** Catalog Import represents an import session for product upsert operations. */
export interface CatalogImport {
  /**
   * CatalogImport ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Date and time the CatalogImport was created.
   * @readonly
   */
  _createdDate?: Date | null;
}

export interface CreateCatalogImportRequest {
  /** CatalogImport to be created. */
  catalogImport: CatalogImport;
}

export interface CreateCatalogImportResponse {
  /** Created CatalogImport. */
  catalogImport?: CatalogImport;
}

export interface AddProductsToImportRequest {
  /**
   * Catalog Import ID.
   * @format GUID
   */
  catalogImportId: string;
  /**
   * List of product items to add to the import session.
   * @minSize 1
   * @maxSize 100
   */
  products: ProductItem[];
}

export interface ProductItem {
  /** Product to upsert - handle field is required for identification */
  product?: ProductWithInventory;
  /**
   * Categories to set for the product.
   * Overrides any existing category assignments.
   */
  categoryIdsInfo?: ProductCategoryIdsInfo;
  /**
   * Explicit list of fields to update in product
   * @internal
   */
  fieldMask?: string[];
}

export interface ProductWithInventory
  extends ProductWithInventoryTypedPropertiesOneOf {
  /**
   * Physical properties.
   *
   * Required when `productType: PHYSICAL`.
   */
  physicalProperties?: PhysicalProperties;
  /**
   * Product ID.
   * @minLength 1
   * @maxLength 36
   * @immutable
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the product is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the product.
   *
   * Ignored when creating a product with inventory.
   * @readonly
   */
  revision?: string | null;
  /**
   * Product name. Translatable.
   * @minLength 1
   * @maxLength 80
   */
  name?: string | null;
  /**
   * Product slug.
   *
   * If not provided, the slug is autogenerated based on the product name.
   * @minLength 1
   * @maxLength 300
   */
  slug?: string | null;
  /**
   * Product description using rich content.
   *
   * Learn more about [Working with Rich Content](https://dev.wix.com/docs/go-headless/tutorials-templates/other-tutorials/working-with-rich-content).
   * @internal
   */
  description?: RichContent;
  /**
   * Product description in HTML.
   *
   * + When provided on create/update, this string must be valid HTML. It's then converted to rich content.
   * + `plainDescription` is ignored when a value is also passed to the `description` field.
   * > **Note:** Returned only when you pass `"PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.
   * @maxLength 16000
   */
  plainDescription?: string | null;
  /**
   * Whether the product is visible to site visitors.
   *
   * Default: `true`
   */
  visible?: boolean | null;
  /**
   * Whether the product is visible in POS (point of sale).
   *
   * Default: `true`
   * > **Note:** Always `false` for `productType: DIGITAL`.
   */
  visibleInPos?: boolean | null;
  /** Product media items. */
  media?: Media;
  /** Product SEO data. */
  seoData?: SeoSchema;
  /**
   * [Tax group ID](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/tax-groups/introduction). Used to apply specific tax rates to products.
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Product options, such as "Size" or "Color". Options define the ways a product can vary.
   *
   * When you provide options, you must also provide the corresponding variants. Each variant must have exactly one choice for each option. For more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).
   *
   * Options are stored as reusable [customization](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/introduction) entities. Pass an existing customization's ID to reuse it, or define options inline and new customization entities are created automatically.
   * @maxSize 6
   */
  options?: ConnectedOption[];
  /**
   * Product modifiers. Collect additional information from customers without creating variants.
   *
   * Unlike options, modifiers don't affect inventory or create additional variants. Use them for things like gift messages or engraving text. For more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).
   *
   * Modifiers are stored as reusable [customization](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/introduction) entities. Pass an existing customization's ID to reuse it, or define modifiers inline and new customization entities are created automatically.
   * @maxSize 10
   */
  modifiers?: ConnectedModifier[];
  /**
   * Product brand.
   *
   * + Pass `brand.name` to add a new brand while creating a product.
   * + Pass an existing brand's `id` to assign that brand to the product.
   */
  brand?: Brand;
  /**
   * Product info section.
   *
   * + Pass `infoSection.uniqueName`, `infoSection.title`, and `infoSection.description` to add a new info section while creating a product.
   * + Pass an existing info section's `id` or `uniqueName` to assign that info section to the product.
   * @maxSize 20
   */
  infoSections?: InfoSection[];
  /**
   * Primary product ribbon displayed on the product. Use `additionalRibbons` to assign further ribbons beyond the primary one.
   *
   * + Pass `ribbon.name` to add a new ribbon while creating a product.
   * + Pass an existing ribbon's `id` or `name` to assign that ribbon to the product.
   */
  ribbon?: Ribbon;
  /**
   * Additional [ribbons](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/ribbons-v3/introduction) displayed on the product, beyond the primary `ribbon`.
   *
   * Use this field to highlight multiple product attributes at once, for example combining a "New" ribbon with a "Sale" ribbon. Both the primary and additional ribbons are rendered on product cards and on the product page. Ribbons appear in the same order as this list.
   *
   * Each entry follows the same assignment rules as `ribbon`:
   * + Pass `additionalRibbons.name` to add a new ribbon while creating or updating the product.
   * + Pass an existing ribbon's `id` or `name` to assign that ribbon to the product.
   *
   * A ribbon can be assigned to a product at most once: the same ribbon can't appear more than once in `additionalRibbons`, and a ribbon set as `ribbon` can't also appear in `additionalRibbons`.
   *
   * Supports up to 4 additional ribbons per product.
   * @maxSize 4
   */
  additionalRibbons?: Ribbon[];
  /**
   * The ID of the product's primary direct category, which defines the product's breadcrumbs path. For example, if the product's main category is "T-Shirts" (which is a subcategory of "Clothing"), the breadcrumbs path will be "Clothing > T-Shirts".
   * @format GUID
   */
  mainCategoryId?: string | null;
  /**
   * Product type: `PHYSICAL` for tangible goods that require shipping, or `DIGITAL` for downloadable content.
   *
   * When passing `productType: PHYSICAL`, you must also pass `physicalProperties`. When passing `productType: DIGITAL`, attach a file by uploading it using [Generate File Upload URL](https://dev.wix.com/docs/api-reference/assets/media/media-manager/files/generate-file-upload-url) and passing the resulting `file.id` from the upload response as `digitalProperties.digitalFile.id` in each variant. A digital product created without a file cannot be sold.
   * @immutable
   */
  productType?: ProductTypeWithLiterals;
  /**
   * A unique human-friendly identifier for the product.
   *
   * Unlike the auto-generated `id`, the handle can be set when creating a product. This is useful when re-importing products from other systems, as it provides a stable identifier across platforms. If not provided during creation, one is automatically generated. Can't be changed after creation.
   * @minLength 1
   * @maxLength 100
   * @readonly
   */
  handle?: string | null;
  /**
   * ID of the last import job that affected the product.
   * @internal
   * @format GUID
   */
  importId?: string | null;
  /**
   * Optional - ID of the app that manages the inventory for this product.
   * @internal
   * @format GUID
   */
  inventoryAppId?: string | null;
  /**
   * Product variants. Each variant represents a specific purchasable version of a product defined by option choices.
   *
   * Each variant must reference all product options via its `choices` array, using `optionChoiceNames` in requests.
   * Variants aren't auto-generated. You must explicitly provide each variant when creating products with options.
   *
   * For more information, see [About Product Options and Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/about-product-options-and-variants).
   */
  variantsInfo?: VariantsInfo;
  /**
   * Product subscriptions.
   *
   * Subscription discounts are defined at the product level but apply to each variant's `actualPrice`. This means the final subscription price varies per variant based on the variant's base price.
   */
  subscriptionDetails?: SubscriptionDetails;
  /** Purchase eligibility settings for the product. */
  purchaseEligibility?: PurchaseEligibility;
  /**
   * Custom extended fields for the product object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the [app dashboard](https://dev.wix.com/dc3/my-apps/) before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /** Tags */
  tags?: Tags;
}

/** @oneof */
export interface ProductWithInventoryTypedPropertiesOneOf {
  /**
   * Physical properties.
   *
   * Required when `productType: PHYSICAL`.
   */
  physicalProperties?: PhysicalProperties;
}

export interface RichContent {
  /** Node objects representing a rich content document. */
  nodes?: Node[];
  /** Object metadata. */
  metadata?: Metadata;
  /** Global styling for header, paragraph, block quote, and code block nodes in the object. */
  documentStyle?: DocumentStyle;
}

export interface Node extends NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Data for a smart block node. */
  smartBlockData?: SmartBlockData;
  /** Data for a smart block cell node. */
  smartBlockCellData?: SmartBlockCellData;
  /** Data for a checkbox list node. */
  checkboxListData?: CheckboxListData;
  /** Data for a list item node. */
  listItemData?: ListItemNodeData;
  /** Node type. Use `APP_EMBED` for nodes that embed content from other Wix apps. Use `EMBED` to embed content in [oEmbed](https://oembed.com/) format. */
  type?: NodeTypeWithLiterals;
  /** Node ID. */
  id?: string;
  /** A list of child nodes. */
  nodes?: Node[];
  /** Padding and background color styling for the node. */
  style?: NodeStyle;
}

/** @oneof */
export interface NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Data for a smart block node. */
  smartBlockData?: SmartBlockData;
  /** Data for a smart block cell node. */
  smartBlockCellData?: SmartBlockCellData;
  /** Data for a checkbox list node. */
  checkboxListData?: CheckboxListData;
  /** Data for a list item node. */
  listItemData?: ListItemNodeData;
}

export enum NodeType {
  PARAGRAPH = 'PARAGRAPH',
  TEXT = 'TEXT',
  HEADING = 'HEADING',
  BULLETED_LIST = 'BULLETED_LIST',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST_ITEM = 'LIST_ITEM',
  BLOCKQUOTE = 'BLOCKQUOTE',
  CODE_BLOCK = 'CODE_BLOCK',
  VIDEO = 'VIDEO',
  DIVIDER = 'DIVIDER',
  FILE = 'FILE',
  GALLERY = 'GALLERY',
  GIF = 'GIF',
  HTML = 'HTML',
  IMAGE = 'IMAGE',
  LINK_PREVIEW = 'LINK_PREVIEW',
  /** @deprecated */
  MAP = 'MAP',
  POLL = 'POLL',
  APP_EMBED = 'APP_EMBED',
  BUTTON = 'BUTTON',
  COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST',
  TABLE = 'TABLE',
  EMBED = 'EMBED',
  COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY',
  TABLE_CELL = 'TABLE_CELL',
  TABLE_ROW = 'TABLE_ROW',
  EXTERNAL = 'EXTERNAL',
  AUDIO = 'AUDIO',
  CAPTION = 'CAPTION',
  LAYOUT = 'LAYOUT',
  LAYOUT_CELL = 'LAYOUT_CELL',
  SHAPE = 'SHAPE',
  CARD = 'CARD',
  TOC = 'TOC',
  SMART_BLOCK = 'SMART_BLOCK',
  SMART_BLOCK_CELL = 'SMART_BLOCK_CELL',
  CHECKBOX_LIST = 'CHECKBOX_LIST',
}

/** @enumType */
export type NodeTypeWithLiterals =
  | NodeType
  | 'PARAGRAPH'
  | 'TEXT'
  | 'HEADING'
  | 'BULLETED_LIST'
  | 'ORDERED_LIST'
  | 'LIST_ITEM'
  | 'BLOCKQUOTE'
  | 'CODE_BLOCK'
  | 'VIDEO'
  | 'DIVIDER'
  | 'FILE'
  | 'GALLERY'
  | 'GIF'
  | 'HTML'
  | 'IMAGE'
  | 'LINK_PREVIEW'
  | 'MAP'
  | 'POLL'
  | 'APP_EMBED'
  | 'BUTTON'
  | 'COLLAPSIBLE_LIST'
  | 'TABLE'
  | 'EMBED'
  | 'COLLAPSIBLE_ITEM'
  | 'COLLAPSIBLE_ITEM_TITLE'
  | 'COLLAPSIBLE_ITEM_BODY'
  | 'TABLE_CELL'
  | 'TABLE_ROW'
  | 'EXTERNAL'
  | 'AUDIO'
  | 'CAPTION'
  | 'LAYOUT'
  | 'LAYOUT_CELL'
  | 'SHAPE'
  | 'CARD'
  | 'TOC'
  | 'SMART_BLOCK'
  | 'SMART_BLOCK_CELL'
  | 'CHECKBOX_LIST';

export interface NodeStyle {
  /** The top padding value in pixels. */
  paddingTop?: string | null;
  /** The bottom padding value in pixels. */
  paddingBottom?: string | null;
  /** The background color as a hexadecimal value. */
  backgroundColor?: string | null;
}

export interface ButtonData {
  /** Styling for the button's container. */
  containerData?: PluginContainerData;
  /** The button type. */
  type?: ButtonDataTypeWithLiterals;
  /** Styling for the button. */
  styles?: Styles;
  /** The text to display on the button. */
  text?: string | null;
  /** Button link details. */
  link?: Link;
  /** Node animation. */
  animation?: Animation;
}

/** Background type */
export enum BackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackgroundTypeWithLiterals = BackgroundType | 'COLOR' | 'GRADIENT';

export interface Gradient {
  /** Gradient type. */
  type?: GradientTypeWithLiterals;
  /**
   * Color stops for the gradient.
   * @maxSize 1000
   */
  stops?: Stop[];
  /** Angle in degrees for linear gradient (0-360). */
  angle?: number | null;
  /**
   * Horizontal center position for radial gradient (0-100).
   * @max 100
   */
  centerX?: number | null;
  /**
   * Vertical center position for radial gradient (0-100).
   * @max 100
   */
  centerY?: number | null;
}

/** Gradient type. */
export enum GradientType {
  /** Linear gradient. */
  LINEAR = 'LINEAR',
  /** Radial gradient. */
  RADIAL = 'RADIAL',
}

/** @enumType */
export type GradientTypeWithLiterals = GradientType | 'LINEAR' | 'RADIAL';

/** A single color stop in the gradient. */
export interface Stop {
  /**
   * Stop color as hex value.
   * @maxLength 19
   */
  color?: string | null;
  /** Stop position (0-1). */
  position?: number | null;
}

export interface Border {
  /**
   * Deprecated: Use `borderWidth` in `styles` instead.
   * @deprecated
   */
  width?: number | null;
  /**
   * Deprecated: Use `borderRadius` in `styles` instead.
   * @deprecated
   */
  radius?: number | null;
}

export interface Colors {
  /**
   * Deprecated: Use `textColor` in `styles` instead.
   * @deprecated
   */
  text?: string | null;
  /**
   * Deprecated: Use `borderColor` in `styles` instead.
   * @deprecated
   */
  border?: string | null;
  /**
   * Deprecated: Use `backgroundColor` in `styles` instead.
   * @deprecated
   */
  background?: string | null;
}

/** Background styling (color or gradient) */
export interface Background {
  /** Background type. */
  type?: BackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerDataWidth;
  /** The node's alignment within its container. */
  alignment?: PluginContainerDataAlignmentWithLiterals;
  /** Spoiler cover settings for the node. */
  spoiler?: Spoiler;
  /** The height of the node when it's displayed. */
  height?: Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean | null;
}

export enum WidthType {
  /** Width matches the content width */
  CONTENT = 'CONTENT',
  /** Small Width */
  SMALL = 'SMALL',
  /** Width will match the original asset width */
  ORIGINAL = 'ORIGINAL',
  /** coast-to-coast display */
  FULL_WIDTH = 'FULL_WIDTH',
}

/** @enumType */
export type WidthTypeWithLiterals =
  | WidthType
  | 'CONTENT'
  | 'SMALL'
  | 'ORIGINAL'
  | 'FULL_WIDTH';

export interface PluginContainerDataWidth
  extends PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

/** @oneof */
export interface PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

export enum PluginContainerDataAlignment {
  /** Center Alignment */
  CENTER = 'CENTER',
  /** Left Alignment */
  LEFT = 'LEFT',
  /** Right Alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type PluginContainerDataAlignmentWithLiterals =
  | PluginContainerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean | null;
  /** The description displayed on top of the spoiler cover. */
  description?: string | null;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string | null;
}

export interface Height {
  /** A custom height value in pixels. */
  custom?: string | null;
}

export enum ButtonDataType {
  /** Regular link button */
  LINK = 'LINK',
  /** Triggers custom action that is defined in plugin configuration by the consumer */
  ACTION = 'ACTION',
}

/** @enumType */
export type ButtonDataTypeWithLiterals = ButtonDataType | 'LINK' | 'ACTION';

export interface Styles {
  /**
   * Deprecated: Use `borderWidth` and `borderRadius` instead.
   * @deprecated
   */
  border?: Border;
  /**
   * Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.
   * @deprecated
   */
  colors?: Colors;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /**
   * Deprecated: Use `borderWidth` for normal/hover states instead.
   * @deprecated
   */
  borderWidthHover?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @maxLength 19
   */
  textColor?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  textColorHover?: string | null;
  /**
   * Deprecated: Use `background` instead.
   * @maxLength 19
   * @deprecated
   */
  backgroundColor?: string | null;
  /**
   * Deprecated: Use `backgroundHover` instead.
   * @maxLength 19
   * @deprecated
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
  /** Background styling (color or gradient). */
  background?: Background;
  /** Background styling for hover state (color or gradient). */
  backgroundHover?: Background;
}

export interface Link extends LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
  /**
   * he HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target?: TargetWithLiterals;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string | null;
}

/** @oneof */
export interface LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
}

export enum Target {
  /** Opens the linked document in the same frame as it was clicked (this is default) */
  SELF = 'SELF',
  /** Opens the linked document in a new window or tab */
  BLANK = 'BLANK',
  /** Opens the linked document in the parent frame */
  PARENT = 'PARENT',
  /** Opens the linked document in the full body of the window */
  TOP = 'TOP',
}

/** @enumType */
export type TargetWithLiterals = Target | 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export interface Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean | null;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean | null;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean | null;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean | null;
}

export interface Animation {
  /** Animation kind. */
  type?: TypeWithLiterals;
  /** Entrance animation, played once when the node first enters the viewport (VIEW). */
  entrance?: EntranceAnimation;
  /** Looping animation, repeated indefinitely while the node is on the page. When combined with entrance, the loop starts after the entrance finishes (VIEW). */
  loop?: LoopAnimation;
  /**
   * POINTER carries its fields directly on the union member in the JTD wall, hence
   * flat fields here (ts-proto camelCase must equal the JTD property names). A future
   * kind that also needs an `effect` field would contend for this JSON name.
   */
  effect?: PointerEffect;
  /** How quickly the node settles toward the pointer, in milliseconds (POINTER). */
  transitionDuration?: number | null;
}

export enum Type {
  /** Animations that play while the node is in view. */
  VIEW = 'VIEW',
  /** Animations driven by the visitor's mouse position. */
  POINTER = 'POINTER',
}

/** @enumType */
export type TypeWithLiterals = Type | 'VIEW' | 'POINTER';

export interface EntranceAnimation {
  /** The entrance effect to play. */
  effect?: EntranceEffect;
  /** Effect duration in milliseconds. Each effect has its own default. */
  duration?: number | null;
  /** Delay before the effect starts, in milliseconds. */
  delay?: number | null;
}

export interface EntranceEffect {
  /** The entrance effect type. */
  type?: EntranceEffectTypeWithLiterals;
  /**
   * Direction the effect enters from. Allowed values depend on the effect type (e.g. TOP/RIGHT/BOTTOM/LEFT, the diagonals with or without CENTER, corners, HORIZONTAL/VERTICAL, CLOCKWISE/COUNTER_CLOCKWISE).
   * @maxLength 20
   */
  direction?: string | null;
  /**
   * Clip-path shape: ELLIPSE, CIRCLE, RECTANGLE, or DIAMOND (SHAPE).
   * @maxLength 12
   */
  shape?: string | null;
  /**
   * Motion style: GENTLE, MODERATE, or INTENSE. Only the effects that expose one accept it.
   * @maxLength 100
   */
  easing?: string | null;
}

export enum EntranceEffectType {
  /** Fades in from fully transparent to fully opaque. */
  FADE = 'FADE',
  /** Enters along a 3D arc path. */
  ARC = 'ARC',
  /** Transitions from blurred to sharp. */
  BLUR = 'BLUR',
  /** Bounces into place with an elastic curve. */
  BOUNCE = 'BOUNCE',
  /** Shrinks down from a larger size. */
  DROP = 'DROP',
  /** Expands from a point, scaling to full size. */
  EXPAND = 'EXPAND',
  /** Flips into view with a 3D rotation. */
  FLIP = 'FLIP',
  /** Drifts gently into place. */
  FLOAT = 'FLOAT',
  /** Unfolds from an edge as if hinged. */
  FOLD = 'FOLD',
  /** Glides in smoothly from off-screen. */
  GLIDE = 'GLIDE',
  /** Progressively revealed by an expanding clip-path. */
  REVEAL = 'REVEAL',
  /** Appears through an expanding geometric clip-path shape. */
  SHAPE = 'SHAPE',
  /** Revealed through shutter-like strips. */
  SHUTTERS = 'SHUTTERS',
  /** Slides in from one side. */
  SLIDE = 'SLIDE',
  /** Spins into view while scaling up. */
  SPIN = 'SPIN',
  /** Tilts in from the side with 3D rotation. */
  TILT = 'TILT',
  /** Rotates into view around a corner pivot. */
  TURN = 'TURN',
  /** Expands from its horizontal or vertical center. */
  WINK = 'WINK',
}

/** @enumType */
export type EntranceEffectTypeWithLiterals =
  | EntranceEffectType
  | 'FADE'
  | 'ARC'
  | 'BLUR'
  | 'BOUNCE'
  | 'DROP'
  | 'EXPAND'
  | 'FLIP'
  | 'FLOAT'
  | 'FOLD'
  | 'GLIDE'
  | 'REVEAL'
  | 'SHAPE'
  | 'SHUTTERS'
  | 'SLIDE'
  | 'SPIN'
  | 'TILT'
  | 'TURN'
  | 'WINK';

export interface LoopAnimation {
  /** The looping effect to repeat. */
  effect?: LoopEffect;
  /** Duration of a single iteration in milliseconds. Each effect has its own default. */
  duration?: number | null;
  /** Pause between iterations in milliseconds. */
  iterationDelay?: number | null;
}

export interface LoopEffect {
  /** The looping effect type. */
  type?: LoopEffectTypeWithLiterals;
  /**
   * Direction of the effect. Allowed values depend on the effect type (e.g. TOP/RIGHT/BOTTOM/LEFT, LEFT/RIGHT, HORIZONTAL/VERTICAL with or without CENTER, CLOCKWISE/COUNTER_CLOCKWISE).
   * @maxLength 20
   */
  direction?: string | null;
  /**
   * Motion style: GENTLE, MODERATE, or INTENSE. Only the effects that expose one accept it.
   * @maxLength 100
   */
  easing?: string | null;
}

export enum LoopEffectType {
  /** Bounces up and down in place. */
  BOUNCE = 'BOUNCE',
  /** Scales gently in and out along an axis. */
  BREATHE = 'BREATHE',
  /** Drifts toward one side, then re-enters from the opposite side. */
  CROSS = 'CROSS',
  /** Fades out to fully transparent and back in. */
  FLASH = 'FLASH',
  /** Rotates a full turn around its horizontal or vertical axis. */
  FLIP = 'FLIP',
  /** Folds back and forth around a hinged edge. */
  FOLD = 'FOLD',
  /** Wobbles with a jelly-like skew. */
  JELLO = 'JELLO',
  /** Nudges repeatedly toward a direction. */
  POKE = 'POKE',
  /** Scales up and back down rhythmically. */
  PULSE = 'PULSE',
  /** Stretches and squashes like a rubber band. */
  RUBBER = 'RUBBER',
  /** Rotates continuously around its center. */
  SPIN = 'SPIN',
  /** Swings back and forth around an edge pivot. */
  SWING = 'SWING',
  /** Rocks side to side with an accumulating rotation. */
  WIGGLE = 'WIGGLE',
}

/** @enumType */
export type LoopEffectTypeWithLiterals =
  | LoopEffectType
  | 'BOUNCE'
  | 'BREATHE'
  | 'CROSS'
  | 'FLASH'
  | 'FLIP'
  | 'FOLD'
  | 'JELLO'
  | 'POKE'
  | 'PULSE'
  | 'RUBBER'
  | 'SPIN'
  | 'SWING'
  | 'WIGGLE';

export interface PointerEffect {
  /** The mouse effect type. */
  type?: PointerEffectTypeWithLiterals;
  /** Reacts away from the pointer instead of toward it. */
  inverted?: boolean | null;
  /**
   * One field, two value spaces (the effect type says which): the axis the movement is constrained to — HORIZONTAL, VERTICAL, or BOTH (AIRY, SCALE, SKEW, TRACK, TRACK_3D) — or the edge/center axis the node swivels around — TOP, BOTTOM, LEFT, RIGHT, CENTER_HORIZONTAL, or CENTER_VERTICAL (SWIVEL). Only the effects that expose one accept it.
   * @maxLength 20
   */
  axis?: string | null;
  /**
   * Whether the node grows or shrinks as the pointer nears: UP or DOWN (SCALE).
   * @maxLength 8
   */
  direction?: string | null;
  /**
   * Motion style: GENTLE, MODERATE, or INTENSE.
   * @maxLength 100
   */
  easing?: string | null;
}

export enum PointerEffectType {
  /** Drifts loosely after the pointer with a light rotation. */
  AIRY = 'AIRY',
  /** Squashes and stretches toward the pointer. */
  BLOB = 'BLOB',
  /** Shifts after the pointer while blurring. */
  BLUR = 'BLUR',
  /** Scales as the pointer approaches. */
  SCALE = 'SCALE',
  /** Skews toward the pointer. */
  SKEW = 'SKEW',
  /** Rotates around a pivot edge or axis, following the pointer. */
  SWIVEL = 'SWIVEL',
  /** Tilts in 3D perspective toward the pointer. */
  TILT_3D = 'TILT_3D',
  /** Follows the pointer's position. */
  TRACK = 'TRACK',
  /** Follows the pointer with a 3D depth motion. */
  TRACK_3D = 'TRACK_3D',
}

/** @enumType */
export type PointerEffectTypeWithLiterals =
  | PointerEffectType
  | 'AIRY'
  | 'BLOB'
  | 'BLUR'
  | 'SCALE'
  | 'SKEW'
  | 'SWIVEL'
  | 'TILT_3D'
  | 'TRACK'
  | 'TRACK_3D';

export interface CodeBlockData {
  /** Styling for the code block's text. */
  textStyle?: TextStyle;
  /** Node animation. */
  animation?: Animation;
}

export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment?: TextAlignmentWithLiterals;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string | null;
}

export enum TextAlignment {
  /** browser default, eqivalent to `initial` */
  AUTO = 'AUTO',
  /** Left align */
  LEFT = 'LEFT',
  /** Right align */
  RIGHT = 'RIGHT',
  /** Center align */
  CENTER = 'CENTER',
  /** Text is spaced to line up its left and right edges to the left and right edges of the line box, except for the last line */
  JUSTIFY = 'JUSTIFY',
}

/** @enumType */
export type TextAlignmentWithLiterals =
  | TextAlignment
  | 'AUTO'
  | 'LEFT'
  | 'RIGHT'
  | 'CENTER'
  | 'JUSTIFY';

export interface DividerData {
  /** Styling for the divider's container. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: LineStyleWithLiterals;
  /** Divider width. */
  width?: WidthWithLiterals;
  /** Divider alignment. */
  alignment?: DividerDataAlignmentWithLiterals;
  /** Visual styling for the divider lines. */
  styles?: DividerDataStyles;
  /** Node animation. */
  animation?: Animation;
}

export enum LineCap {
  /** Square line endings. */
  SQUARE = 'SQUARE',
  /** Rounded line endings. */
  ROUND = 'ROUND',
}

/** @enumType */
export type LineCapWithLiterals = LineCap | 'SQUARE' | 'ROUND';

export enum LineStyle {
  /** Single Line */
  SINGLE = 'SINGLE',
  /** Double Line */
  DOUBLE = 'DOUBLE',
  /** Dashed Line */
  DASHED = 'DASHED',
  /** Dotted Line */
  DOTTED = 'DOTTED',
}

/** @enumType */
export type LineStyleWithLiterals =
  | LineStyle
  | 'SINGLE'
  | 'DOUBLE'
  | 'DASHED'
  | 'DOTTED';

export enum Width {
  /** Large line */
  LARGE = 'LARGE',
  /** Medium line */
  MEDIUM = 'MEDIUM',
  /** Small line */
  SMALL = 'SMALL',
}

/** @enumType */
export type WidthWithLiterals = Width | 'LARGE' | 'MEDIUM' | 'SMALL';

export enum DividerDataAlignment {
  /** Center alignment */
  CENTER = 'CENTER',
  /** Left alignment */
  LEFT = 'LEFT',
  /** Right alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type DividerDataAlignmentWithLiterals =
  | DividerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface DividerDataStyles {
  /**
   * Divider color as a hexadecimal value. An alpha channel controls opacity.
   * @maxLength 19
   */
  color?: string | null;
  /** Divider line ending shape. */
  lineCap?: LineCapWithLiterals;
  /**
   * Line thicknesses in pixels, ordered from first line to last line.
   * @maxSize 10
   */
  lineThickness?: number[];
  /** Visible dash length for dashed dividers, in pixels. The pattern is snapped to a whole number of dashes so that the first and last dash touch the divider's edges - narrower containers show fewer dashes at the same size. */
  dashLength?: number | null;
  /** Visible gap between dashes or dots, in pixels. */
  dashGap?: number | null;
  /** Gap between divider lines in pixels. */
  lineGap?: number | null;
}

export interface FileData {
  /** Styling for the file's container. */
  containerData?: PluginContainerData;
  /** The source for the file's data. */
  src?: FileSource;
  /** File name. */
  name?: string | null;
  /** File type. */
  type?: string | null;
  /**
   * Use `sizeInKb` instead.
   * @deprecated
   */
  size?: number | null;
  /** Settings for PDF files. */
  pdfSettings?: PDFSettings;
  /** File MIME type. */
  mimeType?: string | null;
  /** File path. */
  path?: string | null;
  /** File size in KB. */
  sizeInKb?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export enum ViewMode {
  /** No PDF view */
  NONE = 'NONE',
  /** Full PDF view */
  FULL = 'FULL',
  /** Mini PDF view */
  MINI = 'MINI',
}

/** @enumType */
export type ViewModeWithLiterals = ViewMode | 'NONE' | 'FULL' | 'MINI';

export interface FileSource extends FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean | null;
}

/** @oneof */
export interface FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
}

export interface PDFSettings {
  /**
   * PDF view mode. One of the following:
   * `NONE` : The PDF isn't displayed.
   * `FULL` : A full page view of the PDF is displayed.
   * `MINI` : A mini view of the PDF is displayed.
   */
  viewMode?: ViewModeWithLiterals;
  /** Sets whether the PDF download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the PDF print button is disabled. Defaults to `false`. */
  disablePrint?: boolean | null;
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items?: Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Node animation. */
  animation?: Animation;
}

export interface V1Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number | null;
  /** Media height in pixels. */
  height?: number | null;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number | null;
}

export interface Image {
  /** Image file details. */
  media?: V1Media;
  /** Link details for images that are links. */
  link?: Link;
}

export interface Video {
  /** Video file details. */
  media?: V1Media;
  /** Video thumbnail file details. */
  thumbnail?: V1Media;
}

export interface Item extends ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
  /** Item title. */
  title?: string | null;
  /** Item's alternative text. */
  altText?: string | null;
}

/** @oneof */
export interface ItemDataOneOf {
  /** An image item. */
  image?: Image;
  /** A video item. */
  video?: Video;
}

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptionsLayout;
  /** Styling for gallery items. */
  item?: ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: Thumbnails;
}

export enum LayoutType {
  /** Collage type */
  COLLAGE = 'COLLAGE',
  /** Masonry type */
  MASONRY = 'MASONRY',
  /** Grid type */
  GRID = 'GRID',
  /** Thumbnail type */
  THUMBNAIL = 'THUMBNAIL',
  /** Slider type */
  SLIDER = 'SLIDER',
  /** Slideshow type */
  SLIDESHOW = 'SLIDESHOW',
  /** Panorama type */
  PANORAMA = 'PANORAMA',
  /** Column type */
  COLUMN = 'COLUMN',
  /** Magic type */
  MAGIC = 'MAGIC',
  /** Fullsize images type */
  FULLSIZE = 'FULLSIZE',
}

/** @enumType */
export type LayoutTypeWithLiterals =
  | LayoutType
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export enum Orientation {
  /** Rows Orientation */
  ROWS = 'ROWS',
  /** Columns Orientation */
  COLUMNS = 'COLUMNS',
}

/** @enumType */
export type OrientationWithLiterals = Orientation | 'ROWS' | 'COLUMNS';

export enum Crop {
  /** Crop to fill */
  FILL = 'FILL',
  /** Crop to fit */
  FIT = 'FIT',
}

/** @enumType */
export type CropWithLiterals = Crop | 'FILL' | 'FIT';

export enum ThumbnailsAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Right alignment */
  RIGHT = 'RIGHT',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
  /** Left alignment */
  LEFT = 'LEFT',
  /** No thumbnail */
  NONE = 'NONE',
}

/** @enumType */
export type ThumbnailsAlignmentWithLiterals =
  | ThumbnailsAlignment
  | 'TOP'
  | 'RIGHT'
  | 'BOTTOM'
  | 'LEFT'
  | 'NONE';

export interface GalleryOptionsLayout {
  /** Gallery layout type. */
  type?: LayoutTypeWithLiterals;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean | null;
  /** Gallery orientation. */
  orientation?: OrientationWithLiterals;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number | null;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number | null;
}

export interface ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number | null;
  /** Item ratio */
  ratio?: number | null;
  /** Sets how item images are cropped. */
  crop?: CropWithLiterals;
  /** The spacing between items in pixels. */
  spacing?: number | null;
}

export interface Thumbnails {
  /** Thumbnail alignment. */
  placement?: ThumbnailsAlignmentWithLiterals;
  /** Spacing between thumbnails in pixels. */
  spacing?: number | null;
}

export interface GIFData {
  /** Styling for the GIF's container. */
  containerData?: PluginContainerData;
  /** The source of the full size GIF. */
  original?: GIF;
  /** The source of the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /** Type of GIF (Sticker or NORMAL). Defaults to `NORMAL`. */
  gifType?: GIFTypeWithLiterals;
  /** Node animation. */
  animation?: Animation;
}

export interface GIF {
  /**
   * GIF format URL.
   * @format WEB_URL
   */
  gif?: string | null;
  /**
   * MP4 format URL.
   * @format WEB_URL
   */
  mp4?: string | null;
  /**
   * Thumbnail URL.
   * @format WEB_URL
   */
  still?: string | null;
}

export enum GIFType {
  NORMAL = 'NORMAL',
  STICKER = 'STICKER',
}

/** @enumType */
export type GIFTypeWithLiterals = GIFType | 'NORMAL' | 'STICKER';

export interface HeadingData {
  /** Heading level from 1-6. */
  level?: number;
  /** Styling for the heading text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Rendered heading level for SEO/accessibility, overrides the HTML tag when set. */
  renderedLevel?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface HTMLData extends HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
  /** Styling for the HTML node's container. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /** The type of HTML code. */
  source?: SourceWithLiterals;
  /** If container height is aligned with its content height. Defaults to `true`. */
  autoHeight?: boolean | null;
  /** Node animation. */
  animation?: Animation;
}

/** @oneof */
export interface HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
}

export enum Source {
  HTML = 'HTML',
  ADSENSE = 'ADSENSE',
  AI = 'AI',
  AI_WIDGET = 'AI_WIDGET',
}

/** @enumType */
export type SourceWithLiterals =
  | Source
  | 'HTML'
  | 'ADSENSE'
  | 'AI'
  | 'AI_WIDGET';

export interface ImageData {
  /** Styling for the image's container. */
  containerData?: PluginContainerData;
  /** Image file details. */
  image?: V1Media;
  /** Link details for images that are links. */
  link?: Link;
  /** Sets whether the image expands to full screen when clicked. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Image's alternative text. */
  altText?: string | null;
  /**
   * Deprecated: use Caption node instead.
   * @deprecated
   */
  caption?: string | null;
  /** Sets whether the image's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean | null;
  /** Styling for the image. */
  styles?: ImageDataStyles;
  /** Non-destructive crop rectangle, expressed as fractions (0-1) of the original image. When omitted, the full image is shown. */
  crop?: ImageDataCrop;
  /** Optional shape mask applied to the visible crop. Supported values: CIRCLE, OVAL, STAR, PENTAGON, HEXAGON, TRIANGLE, HEART, RHOMBUS, FLUID, WINDOW. */
  cropShape?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export interface StylesBorder {
  /** Border width in pixels. */
  width?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Border radius in pixels. */
  radius?: number | null;
}

export interface ImageDataStyles {
  /** Border attributes. */
  border?: StylesBorder;
}

export interface ImageDataCrop {
  /** Left edge of the crop, as a fraction (0-1) of the original image width. */
  x?: number | null;
  /** Top edge of the crop, as a fraction (0-1) of the original image height. */
  y?: number | null;
  /** Visible width of the crop, as a fraction (0-1) of the original image width. */
  width?: number | null;
  /** Visible height of the crop, as a fraction (0-1) of the original image height. */
  height?: number | null;
}

export interface LinkPreviewData {
  /** Styling for the link preview's container. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string | null;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string | null;
  /** Preview description. */
  description?: string | null;
  /** The preview content as HTML. */
  html?: string | null;
  /** Styling for the link preview. */
  styles?: LinkPreviewDataStyles;
  /** Node animation. */
  animation?: Animation;
}

export enum StylesPosition {
  /** Thumbnail positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Thumbnail positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Thumbnail positioned at the top */
  TOP = 'TOP',
  /** Thumbnail hidden and not displayed */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StylesPositionWithLiterals =
  | StylesPosition
  | 'START'
  | 'END'
  | 'TOP'
  | 'HIDDEN';

export interface LinkPreviewDataStyles {
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Title color as a hexadecimal value.
   * @maxLength 19
   */
  titleColor?: string | null;
  /**
   * Subtitle color as a hexadecimal value.
   * @maxLength 19
   */
  subtitleColor?: string | null;
  /**
   * Link color as a hexadecimal value.
   * @maxLength 19
   */
  linkColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: StylesPositionWithLiterals;
}

export interface MapData {
  /** Styling for the map's container. */
  containerData?: PluginContainerData;
  /** Map settings. */
  mapSettings?: MapSettings;
}

export interface MapSettings {
  /** The address to display on the map. */
  address?: string | null;
  /** Sets whether the map is draggable. */
  draggable?: boolean | null;
  /** Sets whether the location marker is visible. */
  marker?: boolean | null;
  /** Sets whether street view control is enabled. */
  streetViewControl?: boolean | null;
  /** Sets whether zoom control is enabled. */
  zoomControl?: boolean | null;
  /** Location latitude. */
  lat?: number | null;
  /** Location longitude. */
  lng?: number | null;
  /** Location name. */
  locationName?: string | null;
  /** Sets whether view mode control is enabled. */
  viewModeControl?: boolean | null;
  /** Initial zoom value. */
  initialZoom?: number | null;
  /** Map type. `HYBRID` is a combination of the `ROADMAP` and `SATELLITE` map types. */
  mapType?: MapTypeWithLiterals;
}

export enum MapType {
  /** Roadmap map type */
  ROADMAP = 'ROADMAP',
  /** Satellite map type */
  SATELITE = 'SATELITE',
  /** Hybrid map type */
  HYBRID = 'HYBRID',
  /** Terrain map type */
  TERRAIN = 'TERRAIN',
}

/** @enumType */
export type MapTypeWithLiterals =
  | MapType
  | 'ROADMAP'
  | 'SATELITE'
  | 'HYBRID'
  | 'TERRAIN';

export interface ParagraphData {
  /** Styling for the paragraph text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Paragraph level */
  level?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface PollData {
  /** Styling for the poll's container. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: Poll;
  /** Layout settings for the poll and voting options. */
  layout?: PollDataLayout;
  /** Styling for the poll and voting options. */
  design?: Design;
  /** Node animation. */
  animation?: Animation;
}

export enum ViewRole {
  /** Only Poll creator can view the results */
  CREATOR = 'CREATOR',
  /** Anyone who voted can see the results */
  VOTERS = 'VOTERS',
  /** Anyone can see the results, even if one didn't vote */
  EVERYONE = 'EVERYONE',
}

/** @enumType */
export type ViewRoleWithLiterals = ViewRole | 'CREATOR' | 'VOTERS' | 'EVERYONE';

export enum VoteRole {
  /** Logged in member */
  SITE_MEMBERS = 'SITE_MEMBERS',
  /** Anyone */
  ALL = 'ALL',
}

/** @enumType */
export type VoteRoleWithLiterals = VoteRole | 'SITE_MEMBERS' | 'ALL';

export interface Permissions {
  /** Sets who can view the poll results. */
  view?: ViewRoleWithLiterals;
  /** Sets who can vote. */
  vote?: VoteRoleWithLiterals;
  /** Sets whether one voter can vote multiple times. Defaults to `false`. */
  allowMultipleVotes?: boolean | null;
}

export interface Option {
  /** Option ID. */
  id?: string | null;
  /** Option title. */
  title?: string | null;
  /** The image displayed with the option. */
  image?: V1Media;
}

export interface PollSettings {
  /** Permissions settings for voting. */
  permissions?: Permissions;
  /** Sets whether voters are displayed in the vote results. Defaults to `true`. */
  showVoters?: boolean | null;
  /** Sets whether the vote count is displayed. Defaults to `true`. */
  showVotesCount?: boolean | null;
}

export enum PollLayoutType {
  /** List */
  LIST = 'LIST',
  /** Grid */
  GRID = 'GRID',
}

/** @enumType */
export type PollLayoutTypeWithLiterals = PollLayoutType | 'LIST' | 'GRID';

export enum PollLayoutDirection {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type PollLayoutDirectionWithLiterals =
  | PollLayoutDirection
  | 'LTR'
  | 'RTL';

export interface PollLayout {
  /** The layout for displaying the voting options. */
  type?: PollLayoutTypeWithLiterals;
  /** The direction of the text displayed in the voting options. Text can be displayed either right-to-left or left-to-right. */
  direction?: PollLayoutDirectionWithLiterals;
  /** Sets whether to display the main poll image. Defaults to `false`. */
  enableImage?: boolean | null;
}

export interface OptionLayout {
  /** Sets whether to display option images. Defaults to `false`. */
  enableImage?: boolean | null;
}

export enum PollDesignBackgroundType {
  /** Color background type */
  COLOR = 'COLOR',
  /** Image background type */
  IMAGE = 'IMAGE',
  /** Gradiant background type */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type PollDesignBackgroundTypeWithLiterals =
  | PollDesignBackgroundType
  | 'COLOR'
  | 'IMAGE'
  | 'GRADIENT';

export interface BackgroundGradient {
  /** The gradient angle in degrees. */
  angle?: number | null;
  /**
   * The start color as a hexademical value.
   * @maxLength 19
   */
  startColor?: string | null;
  /**
   * The end color as a hexademical value.
   * @maxLength 19
   */
  lastColor?: string | null;
}

export interface PollDesignBackground
  extends PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @maxLength 19
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: V1Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
  /** Background type. For each option, include the relevant details. */
  type?: PollDesignBackgroundTypeWithLiterals;
}

/** @oneof */
export interface PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @maxLength 19
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: V1Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
}

export interface PollDesign {
  /** Background styling. */
  background?: PollDesignBackground;
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface OptionDesign {
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface Poll {
  /** Poll ID. */
  id?: string | null;
  /** Poll title. */
  title?: string | null;
  /** Poll creator ID. */
  creatorId?: string | null;
  /** Main poll image. */
  image?: V1Media;
  /** Voting options. */
  options?: Option[];
  /** The poll's permissions and display settings. */
  settings?: PollSettings;
}

export interface PollDataLayout {
  /** Poll layout settings. */
  poll?: PollLayout;
  /** Voting otpions layout settings. */
  options?: OptionLayout;
}

export interface Design {
  /** Styling for the poll. */
  poll?: PollDesign;
  /** Styling for voting options. */
  options?: OptionDesign;
}

export interface TextData {
  /** The text to apply decorations to. */
  text?: string;
  /** The decorations to apply. */
  decorations?: Decoration[];
}

/** Adds appearence changes to text */
export interface Decoration extends DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** Data for a hand-drawn sketch annotation decoration. */
  sketchData?: SketchData;
  /** The type of decoration to apply. */
  type?: DecorationTypeWithLiterals;
}

/** @oneof */
export interface DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** Data for a hand-drawn sketch annotation decoration. */
  sketchData?: SketchData;
}

export enum DecorationType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  SPOILER = 'SPOILER',
  ANCHOR = 'ANCHOR',
  MENTION = 'MENTION',
  LINK = 'LINK',
  COLOR = 'COLOR',
  FONT_SIZE = 'FONT_SIZE',
  EXTERNAL = 'EXTERNAL',
  STRIKETHROUGH = 'STRIKETHROUGH',
  SUPERSCRIPT = 'SUPERSCRIPT',
  SUBSCRIPT = 'SUBSCRIPT',
  FONT_FAMILY = 'FONT_FAMILY',
  SKETCH = 'SKETCH',
}

/** @enumType */
export type DecorationTypeWithLiterals =
  | DecorationType
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'EXTERNAL'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT'
  | 'FONT_FAMILY'
  | 'SKETCH';

export interface AnchorData {
  /** The target node's ID. */
  anchor?: string;
}

export interface ColorData {
  /** The text's background color as a hexadecimal value. */
  background?: string | null;
  /** The text's foreground color as a hexadecimal value. */
  foreground?: string | null;
}

export interface LinkData {
  /** Link details. */
  link?: Link;
}

export interface MentionData {
  /** The mentioned user's name. */
  name?: string;
  /** The version of the user's name that appears after the `@` character in the mention. */
  slug?: string;
  /** Mentioned user's ID. */
  id?: string | null;
  /** The kind of entity the mention refers to. Any value is accepted, and it's usually used to mark a mention as a group mention (for example, `group`). When empty, the mention refers to a member. */
  type?: string | null;
}

export interface FontSizeData {
  /** The units used for the font size. */
  unit?: FontTypeWithLiterals;
  /** Font size value. */
  value?: number | null;
}

export enum FontType {
  PX = 'PX',
  EM = 'EM',
}

/** @enumType */
export type FontTypeWithLiterals = FontType | 'PX' | 'EM';

export interface SpoilerData {
  /** Spoiler ID. */
  id?: string | null;
}

export interface FontFamilyData {
  /** @maxLength 1000 */
  value?: string | null;
}

export interface SketchData {
  /** The sketch annotation variant to draw over the text. */
  variant?: VariantWithLiterals;
  /**
   * Annotation color. Defaults to the theme action color.
   * @maxLength 19
   */
  color?: string | null;
  /** Whether the annotation animates on first paint. Defaults to `true`. */
  animate?: boolean | null;
}

export enum Variant {
  UNDERLINE = 'UNDERLINE',
  BOX = 'BOX',
  CIRCLE = 'CIRCLE',
  HIGHLIGHT = 'HIGHLIGHT',
  STRIKETHROUGH = 'STRIKETHROUGH',
  CROSSED_OFF = 'CROSSED_OFF',
}

/** @enumType */
export type VariantWithLiterals =
  | Variant
  | 'UNDERLINE'
  | 'BOX'
  | 'CIRCLE'
  | 'HIGHLIGHT'
  | 'STRIKETHROUGH'
  | 'CROSSED_OFF';

export interface AppEmbedData extends AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
  /** The type of Wix App content being embedded. */
  type?: AppTypeWithLiterals;
  /** The ID of the embedded content. */
  itemId?: string | null;
  /** The name of the embedded content. */
  name?: string | null;
  /**
   * Deprecated: Use `image` instead.
   * @deprecated
   */
  imageSrc?: string | null;
  /** The URL for the embedded content. */
  url?: string | null;
  /** An image for the embedded content. */
  image?: V1Media;
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Whether to hide the title. */
  hideTitle?: boolean | null;
  /** Whether to hide the price. */
  hidePrice?: boolean | null;
  /** Whether to hide the description (Event and Booking). */
  hideDescription?: boolean | null;
  /** Whether to hide the date and time (Event). */
  hideDateTime?: boolean | null;
  /** Whether to hide the location (Event). */
  hideLocation?: boolean | null;
  /** Whether to hide the duration (Booking). */
  hideDuration?: boolean | null;
  /** Whether to hide the button. */
  hideButton?: boolean | null;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean | null;
  /** Button styling options. */
  buttonStyles?: ButtonStyles;
  /** Image styling options. */
  imageStyles?: ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: RibbonStyles;
  /** Card styling options. */
  cardStyles?: CardStyles;
  /** Styling for the app embed's container. */
  containerData?: PluginContainerData;
  /** Pricing data for embedded Wix App content. */
  pricingData?: PricingData;
  /** Node animation. */
  animation?: Animation;
}

/** @oneof */
export interface AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
}

export enum Position {
  /** Image positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Image positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Image positioned at the top */
  TOP = 'TOP',
}

/** @enumType */
export type PositionWithLiterals = Position | 'START' | 'END' | 'TOP';

export enum AspectRatio {
  /** 1:1 aspect ratio */
  SQUARE = 'SQUARE',
  /** 16:9 aspect ratio */
  RECTANGLE = 'RECTANGLE',
}

/** @enumType */
export type AspectRatioWithLiterals = AspectRatio | 'SQUARE' | 'RECTANGLE';

export enum Resizing {
  /** Fill the container, may crop the image */
  FILL = 'FILL',
  /** Fit the image within the container */
  FIT = 'FIT',
}

/** @enumType */
export type ResizingWithLiterals = Resizing | 'FILL' | 'FIT';

export enum Placement {
  /** Ribbon placed on the image */
  IMAGE = 'IMAGE',
  /** Ribbon placed on the product information */
  PRODUCT_INFO = 'PRODUCT_INFO',
}

/** @enumType */
export type PlacementWithLiterals = Placement | 'IMAGE' | 'PRODUCT_INFO';

export enum CardStylesType {
  /** Card with visible border and background */
  CONTAINED = 'CONTAINED',
  /** Card without visible border */
  FRAMELESS = 'FRAMELESS',
}

/** @enumType */
export type CardStylesTypeWithLiterals =
  | CardStylesType
  | 'CONTAINED'
  | 'FRAMELESS';

export enum Alignment {
  /** Content aligned to start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Content centered */
  CENTER = 'CENTER',
  /** Content aligned to end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'START' | 'CENTER' | 'END';

export enum Layout {
  /** Elements stacked vertically */
  STACKED = 'STACKED',
  /** Elements arranged horizontally */
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
}

/** @enumType */
export type LayoutWithLiterals = Layout | 'STACKED' | 'SIDE_BY_SIDE';

export enum AppType {
  PRODUCT = 'PRODUCT',
  EVENT = 'EVENT',
  BOOKING = 'BOOKING',
}

/** @enumType */
export type AppTypeWithLiterals = AppType | 'PRODUCT' | 'EVENT' | 'BOOKING';

export interface BookingData {
  /** Booking duration in minutes. */
  durations?: string | null;
}

export interface EventData {
  /** Event schedule. */
  scheduling?: string | null;
  /** Event location. */
  location?: string | null;
}

export interface ButtonStyles {
  /** Text to display on the button. */
  buttonText?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @maxLength 19
   */
  textColor?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @maxLength 19
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface ImageStyles {
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Position of image. Defaults to `START`. */
  imagePosition?: PositionWithLiterals;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AspectRatioWithLiterals;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: ResizingWithLiterals;
  /**
   * Image border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Image border width in pixels. */
  borderWidth?: number | null;
  /** Image border radius in pixels. */
  borderRadius?: number | null;
}

export interface RibbonStyles {
  /** Text to display on the ribbon. */
  ribbonText?: string | null;
  /**
   * Ribbon background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Ribbon text color as a hexadecimal value.
   * @maxLength 19
   */
  textColor?: string | null;
  /**
   * Ribbon border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Ribbon border width in pixels. */
  borderWidth?: number | null;
  /** Ribbon border radius in pixels. */
  borderRadius?: number | null;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: PlacementWithLiterals;
}

export interface CardStyles {
  /**
   * Card background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Card border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Card border width in pixels. */
  borderWidth?: number | null;
  /** Card border radius in pixels. */
  borderRadius?: number | null;
  /** Card type. Defaults to `CONTAINED`. */
  type?: CardStylesTypeWithLiterals;
  /** Content alignment. Defaults to `START`. */
  alignment?: AlignmentWithLiterals;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: LayoutWithLiterals;
  /**
   * Title text color as a hexadecimal value.
   * @maxLength 19
   */
  titleColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @maxLength 19
   */
  textColor?: string | null;
}

export interface PricingData {
  /**
   * Minimum numeric price value as string (e.g., "10.99").
   * @decimalValue options { maxScale:2 }
   */
  valueFrom?: string | null;
  /**
   * Maximum numeric price value as string (e.g., "19.99").
   * @decimalValue options { maxScale:2 }
   */
  valueTo?: string | null;
  /**
   * Numeric price value as string after discount application (e.g., "15.99").
   * @decimalValue options { maxScale:2 }
   */
  discountedValue?: string | null;
  /**
   * Currency of the value in ISO 4217 format (e.g., "USD", "EUR").
   * @format CURRENCY
   */
  currency?: string | null;
  /**
   * Pricing plan ID.
   * @format GUID
   */
  pricingPlanId?: string | null;
}

export interface VideoData {
  /** Styling for the video's container. */
  containerData?: PluginContainerData;
  /** Video details. */
  video?: V1Media;
  /** Video thumbnail details. */
  thumbnail?: V1Media;
  /** Sets whether the video's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Video title. */
  title?: string | null;
  /** Video options. */
  options?: PlaybackOptions;
  /** Node animation. */
  animation?: Animation;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean | null;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean | null;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean | null;
}

export interface EmbedData {
  /** Styling for the oEmbed node's container. */
  containerData?: PluginContainerData;
  /** An [oEmbed](https://www.oembed.com) object. */
  oembed?: Oembed;
  /** Origin asset source. */
  src?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export interface Oembed {
  /** The resource type. */
  type?: string | null;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number | null;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number | null;
  /** Resource title. */
  title?: string | null;
  /** The source URL for the resource. */
  url?: string | null;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string | null;
  /** The name of the author or owner of the resource. */
  authorName?: string | null;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string | null;
  /** The name of the resource provider. */
  providerName?: string | null;
  /** The URL for the resource provider. */
  providerUrl?: string | null;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string | null;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string | null;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string | null;
  /** The URL for an embedded viedo. */
  videoUrl?: string | null;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string | null;
}

export interface CollapsibleListData {
  /** Styling for the collapsible list's container. */
  containerData?: PluginContainerData;
  /** If `true`, only one item can be expanded at a time. Defaults to `false`. */
  expandOnlyOne?: boolean | null;
  /** Sets which items are expanded when the page loads. */
  initialExpandedItems?: InitialExpandedItemsWithLiterals;
  /** The direction of the text in the list. Either left-to-right or right-to-left. */
  direction?: DirectionWithLiterals;
  /** If `true`, The collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean | null;
  /** Node animation. */
  animation?: Animation;
}

export enum InitialExpandedItems {
  /** First item will be expended initally */
  FIRST = 'FIRST',
  /** All items will expended initally */
  ALL = 'ALL',
  /** All items collapsed initally */
  NONE = 'NONE',
}

/** @enumType */
export type InitialExpandedItemsWithLiterals =
  | InitialExpandedItems
  | 'FIRST'
  | 'ALL'
  | 'NONE';

export enum Direction {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'LTR' | 'RTL';

export interface TableData {
  /** Styling for the table's container. */
  containerData?: PluginContainerData;
  /** The table's dimensions. */
  dimensions?: TableDataDimensions;
  /**
   * Deprecated: Use `rowHeader` and `columnHeader` instead.
   * @deprecated
   */
  header?: boolean | null;
  /** Sets whether the table's first row is a header. Defaults to `false`. */
  rowHeader?: boolean | null;
  /** Sets whether the table's first column is a header. Defaults to `false`. */
  columnHeader?: boolean | null;
  /** The spacing between cells in pixels. Defaults to `0`. */
  cellSpacing?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left.
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Table's alternative text. */
  altText?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export interface TableDataDimensions {
  /** An array representing relative width of each column in relation to the other columns. */
  colsWidthRatio?: number[];
  /** An array representing the height of each row in pixels. */
  rowsHeight?: number[];
  /** An array representing the minimum width of each column in pixels. */
  colsMinWidth?: number[];
}

export interface TableCellData {
  /** Styling for the cell's background color and text alignment. */
  cellStyle?: CellStyle;
  /** The cell's border colors. */
  borderColors?: BorderColors;
  /** Defines how many columns the cell spans. Default: 1. */
  colspan?: number | null;
  /** Defines how many rows the cell spans. Default: 1. */
  rowspan?: number | null;
  /** The cell's border widths. */
  borderWidths?: BorderWidths;
}

export enum VerticalAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentWithLiterals =
  | VerticalAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export interface CellStyle {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignmentWithLiterals;
  /**
   * Cell background color as a hexadecimal value.
   * @maxLength 19
   */
  backgroundColor?: string | null;
}

export interface BorderColors {
  /**
   * Left border color as a hexadecimal value.
   * @maxLength 19
   */
  left?: string | null;
  /**
   * Right border color as a hexadecimal value.
   * @maxLength 19
   */
  right?: string | null;
  /**
   * Top border color as a hexadecimal value.
   * @maxLength 19
   */
  top?: string | null;
  /**
   * Bottom border color as a hexadecimal value.
   * @maxLength 19
   */
  bottom?: string | null;
}

export interface BorderWidths {
  /** Left border width in pixels. */
  left?: number | null;
  /** Right border width in pixels. */
  right?: number | null;
  /** Top border width in pixels. */
  top?: number | null;
  /** Bottom border width in pixels. */
  bottom?: number | null;
}

/**
 * `NullValue` is a singleton enumeration to represent the null value for the
 * `Value` type union.
 *
 * The JSON representation for `NullValue` is JSON `null`.
 */
export enum NullValue {
  /** Null value. */
  NULL_VALUE = 'NULL_VALUE',
}

/** @enumType */
export type NullValueWithLiterals = NullValue | 'NULL_VALUE';

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values?: any[];
}

export interface AudioData {
  /** Styling for the audio node's container. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: V1Media;
  /** Sets whether the audio node's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Cover image. */
  coverImage?: V1Media;
  /** Track name. */
  name?: string | null;
  /** Author name. */
  authorName?: string | null;
  /** An HTML version of the audio node. */
  html?: string | null;
  /** Node animation. */
  animation?: Animation;
}

export interface OrderedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** List start number. */
  start?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface BulletedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface BlockquoteData {
  /** Indentation level from 1-4. */
  indentation?: number;
  /** Node animation. */
  animation?: Animation;
}

export interface CaptionData {
  textStyle?: TextStyle;
}

export interface LayoutData {
  /**
   * Deprecated: Use `background` instead.
   * @maxLength 19
   * @deprecated
   */
  backgroundColor?: string | null;
  /** Background image. */
  backgroundImage?: LayoutDataBackgroundImage;
  /**
   * Border color as a hexadecimal value.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Deprecated: Use `backdrop` instead.
   * @maxLength 19
   * @deprecated
   */
  backdropColor?: string | null;
  /** Backdrop image. */
  backdropImage?: LayoutDataBackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number | null;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number | null;
  /** Horizontal and vertical gap between columns */
  gap?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignmentAlignmentWithLiterals;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour?: ResponsivenessBehaviourWithLiterals;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number | null;
  /** Styling for the layout's container. */
  containerData?: PluginContainerData;
  /** Defines where selected design propertied applies to */
  designTarget?: DesignTargetWithLiterals;
  /** Banner configuration. When present, this layout is attached to a document edge (top or bottom). */
  banner?: Banner;
  /** Background styling (color or gradient). */
  background?: LayoutDataBackground;
  /** Backdrop styling (color or gradient). */
  backdrop?: Backdrop;
  /** Node animation. */
  animation?: Animation;
  /** Shape divider at the top edge of the layout. */
  topDivider?: SectionDivider;
  /** Shape divider at the bottom edge of the layout. */
  bottomDivider?: SectionDivider;
}

export enum ImageScalingScaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ImageScalingScalingWithLiterals =
  | ImageScalingScaling
  | 'AUTO'
  | 'CONTAIN'
  | 'COVER';

export enum ImagePosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionWithLiterals =
  | ImagePosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

/** Background styling (color or gradient) */
export interface LayoutDataBackground {
  /** Background type. */
  type?: LayoutDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Background type */
export enum LayoutDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type LayoutDataBackgroundTypeWithLiterals =
  | LayoutDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

export enum Origin {
  /** Banner originated from an image */
  IMAGE = 'IMAGE',
  /** Banner originated from a layout */
  LAYOUT = 'LAYOUT',
}

/** @enumType */
export type OriginWithLiterals = Origin | 'IMAGE' | 'LAYOUT';

export enum BannerPosition {
  /** Attached to the top edge (banner) */
  TOP = 'TOP',
  /** Attached to the bottom edge (footer) */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type BannerPositionWithLiterals = BannerPosition | 'TOP' | 'BOTTOM';

/** How the stacked layers fade relative to the base shape. Named for a bottom divider; top dividers render mirrored, so the visual fade flips. */
export enum LayerEffect {
  /** Shape keeps its height, a solid filler bar pushes it away from the edge */
  CENTER = 'CENTER',
  /** Deeper (more transparent) layers shrink toward the edge inside the original band while a solid filler bar keeps its full height, so the faded copies hug the top of the band */
  FADE_TO_TOP = 'FADE_TO_TOP',
  /** Deeper (more transparent) layers stretch taller into the section, so the faded copies fan out above the base shape and descend to the layout edge */
  FADE_TO_BOTTOM = 'FADE_TO_BOTTOM',
}

/** @enumType */
export type LayerEffectWithLiterals =
  | LayerEffect
  | 'CENTER'
  | 'FADE_TO_TOP'
  | 'FADE_TO_BOTTOM';

/** Divider shape preset */
export enum Shape {
  /** Ellipse shape */
  ELLIPSE = 'ELLIPSE',
  /** Tilt shape */
  TILT = 'TILT',
  /** Liquid shape */
  LIQUID = 'LIQUID',
  /** Left wave shape */
  LEFT_WAVE = 'LEFT_WAVE',
  /** Paint scribble shape */
  PAINT_SCRIBBLE = 'PAINT_SCRIBBLE',
  /** Inverted ellipse shape */
  INVERTED_ELLIPSE = 'INVERTED_ELLIPSE',
  /** Right wave shape */
  RIGHT_WAVE = 'RIGHT_WAVE',
  /** Dunes shape */
  DUNES = 'DUNES',
  /** Waves shape */
  WAVES = 'WAVES',
  /** Triangle shape */
  TRIANGLE = 'TRIANGLE',
  /** Semi-ellipse shape */
  SEMI_ELLIPSE = 'SEMI_ELLIPSE',
  /** Plants shape */
  PLANTS = 'PLANTS',
  /** Layered ellipse shape */
  LAYERED_ELLIPSE = 'LAYERED_ELLIPSE',
  /** Pixels shape */
  PIXELS = 'PIXELS',
  /** Paint shape */
  PAINT = 'PAINT',
  /** Clouds shape */
  CLOUDS = 'CLOUDS',
  /** Optical illusion shape */
  OPTICAL_ILLUSION = 'OPTICAL_ILLUSION',
  /** Stripes shape */
  STRIPES = 'STRIPES',
  /** Blobs shape */
  BLOBS = 'BLOBS',
  /** Semi-circles shape */
  SEMI_CIRCLES = 'SEMI_CIRCLES',
  /** Hill shape */
  HILL = 'HILL',
  /** Brush shape */
  BRUSH = 'BRUSH',
  /** Peaks shape */
  PEAKS = 'PEAKS',
  /** Angled triangle shape */
  ANGLED_TRIANGLE = 'ANGLED_TRIANGLE',
}

/** @enumType */
export type ShapeWithLiterals =
  | Shape
  | 'ELLIPSE'
  | 'TILT'
  | 'LIQUID'
  | 'LEFT_WAVE'
  | 'PAINT_SCRIBBLE'
  | 'INVERTED_ELLIPSE'
  | 'RIGHT_WAVE'
  | 'DUNES'
  | 'WAVES'
  | 'TRIANGLE'
  | 'SEMI_ELLIPSE'
  | 'PLANTS'
  | 'LAYERED_ELLIPSE'
  | 'PIXELS'
  | 'PAINT'
  | 'CLOUDS'
  | 'OPTICAL_ILLUSION'
  | 'STRIPES'
  | 'BLOBS'
  | 'SEMI_CIRCLES'
  | 'HILL'
  | 'BRUSH'
  | 'PEAKS'
  | 'ANGLED_TRIANGLE';

/** Layers effect - stacked semi-transparent copies of the divider shape */
export interface Layers {
  /** Total number of layers including the base shape (1-4). Absent or 1 disables the effect. */
  count?: number | null;
  /** Vertical distance in pixels between the silhouettes of consecutive layers: each successive layer's silhouette is offset from the previous one by this amount. The divider's own `height` is unchanged. Defaults to 20. */
  offsetY?: number | null;
  /** How the stacked layers fade relative to the base shape. */
  layerEffect?: LayerEffectWithLiterals;
  /** Horizontal distance between consecutive layers in pixels. Defaults to 0. Only applies to shapes that support horizontal offset. */
  offsetX?: number | null;
}

/** Backdrop type */
export enum BackdropType {
  /** Solid color backdrop */
  COLOR = 'COLOR',
  /** Gradient backdrop */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackdropTypeWithLiterals = BackdropType | 'COLOR' | 'GRADIENT';

export interface LayoutDataBackgroundImage {
  /** Background image. */
  media?: V1Media;
  /**
   * Deprecated: use `overlay` instead. Legacy image opacity (0–100) that dimmed the image to reveal the `background`/`backdrop` color behind it.
   * @deprecated
   */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ImageScalingScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionWithLiterals;
  /** Blur radius in pixels applied to the image layer. `0` (default) leaves the image unblurred; blur is independent of any color overlay and the two stack. */
  blur?: number | null;
  /** Color or gradient drawn on top of the image. When present, this is the authoritative overlay and `opacity` is ignored. Its presence also marks content as authored under the new overlay model (vs. the legacy `opacity`-based dimming on `background`/`backdrop`). */
  overlay?: LayoutDataBackground;
}

export enum VerticalAlignmentAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentAlignmentWithLiterals =
  | VerticalAlignmentAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export enum ResponsivenessBehaviour {
  /** Stacking of columns */
  STACK = 'STACK',
  /** Wrapping of columns */
  WRAP = 'WRAP',
}

/** @enumType */
export type ResponsivenessBehaviourWithLiterals =
  | ResponsivenessBehaviour
  | 'STACK'
  | 'WRAP';

export enum DesignTarget {
  /** Design applied to layout */
  LAYOUT = 'LAYOUT',
  /** Design applied to cells */
  CELL = 'CELL',
}

/** @enumType */
export type DesignTargetWithLiterals = DesignTarget | 'LAYOUT' | 'CELL';

export interface Banner {
  /** Origin of the banner */
  origin?: OriginWithLiterals;
  /** Position of the banner */
  position?: BannerPositionWithLiterals;
}

/** Backdrop styling (color or gradient) */
export interface Backdrop {
  /** Backdrop type. */
  type?: BackdropTypeWithLiterals;
  /**
   * Backdrop color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Decorative shape divider at a layout edge */
export interface SectionDivider {
  /** Divider shape preset. Determines which customization properties apply. */
  type?: ShapeWithLiterals;
  /**
   * Divider fill color as a hexadecimal value (may include alpha).
   * @maxLength 19
   */
  color?: string | null;
  /** Divider height in pixels. */
  height?: number | null;
  /** Horizontal offset in pixels. */
  offsetX?: number | null;
  /** Whether the shape is flipped horizontally. */
  flipped?: boolean | null;
  /** Layers effect - stacked semi-transparent copies of the divider shape. */
  layers?: Layers;
}

export interface LayoutCellData {
  /** Size of the cell in 12 columns grid. */
  colSpan?: number | null;
}

export interface ShapeData {
  /** Styling for the shape's container. */
  containerData?: PluginContainerData;
  /** Shape file details. */
  shape?: V1Media;
  /** Styling for the shape. */
  styles?: ShapeDataStyles;
  /** Node animation. */
  animation?: Animation;
  /** Link details for shapes that are links. */
  link?: Link;
  /**
   * Alternate text describing the link's purpose for accessibility. Applies only when `link` is set.
   * @maxLength 1000
   */
  altText?: string | null;
}

export interface ShapeDataStyles {
  /**
   * Shape fill color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Map of original color keys to their new color values. */
  colors?: Record<string, string>;
}

export interface CardData {
  /** Background styling (color or gradient). */
  background?: CardDataBackground;
  /** Background image. */
  backgroundImage?: BackgroundImage;
}

export enum Scaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ScalingWithLiterals = Scaling | 'AUTO' | 'CONTAIN' | 'COVER';

export enum ImagePositionPosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionPositionWithLiterals =
  | ImagePositionPosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

/** Background styling (color or gradient) */
export interface CardDataBackground {
  /** Background type. */
  type?: CardDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @maxLength 19
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Background type */
export enum CardDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type CardDataBackgroundTypeWithLiterals =
  | CardDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

export interface BackgroundImage {
  /** Background image. */
  media?: V1Media;
  /**
   * Deprecated: use `overlay` instead. Legacy image opacity (0–100) that dimmed the image to reveal the `background` color behind it.
   * @deprecated
   */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionPositionWithLiterals;
  /** Color or gradient drawn on top of the image. When present, this is the authoritative overlay and `opacity` is ignored. Its presence also marks content as authored under the new overlay model (vs. the legacy `opacity`-based dimming on `background`). */
  overlay?: CardDataBackground;
  /** Blur radius in pixels applied to the image layer. `0` (default) leaves the image unblurred; blur is independent of any color overlay and the two stack. */
  blur?: number | null;
}

export interface TocData {
  /** Heading levels included in the table of contents. Default: [1, 2, 3, 4, 5, 6]. */
  includedHeadings?: number[];
  /** List style. Default: PLAIN. */
  listStyle?: ListStyleWithLiterals;
  /** Optional override for the font size in pixels. */
  fontSize?: number | null;
  /** Optional override for the vertical spacing between items in pixels. */
  itemSpacing?: number | null;
  /**
   * Optional override for the text color.
   * @maxLength 19
   */
  color?: string | null;
  /** Indentation style. Default: NESTED. */
  indentation?: IndentationWithLiterals;
  /** Node animation. */
  animation?: Animation;
}

/** List style. */
export enum ListStyle {
  /** No markers (default) */
  PLAIN = 'PLAIN',
  /** Numbered list */
  NUMBERED = 'NUMBERED',
  /** Alphabetic letters */
  LETTERS = 'LETTERS',
  /** Roman numerals */
  ROMAN = 'ROMAN',
  /** Bulleted list */
  BULLETED = 'BULLETED',
  /** Alphabetical index */
  ALPHABETICAL_INDEX = 'ALPHABETICAL_INDEX',
  /** Alphabetical index (compact top-row only) */
  ALPHABETICAL_INDEX_COMPACT = 'ALPHABETICAL_INDEX_COMPACT',
}

/** @enumType */
export type ListStyleWithLiterals =
  | ListStyle
  | 'PLAIN'
  | 'NUMBERED'
  | 'LETTERS'
  | 'ROMAN'
  | 'BULLETED'
  | 'ALPHABETICAL_INDEX'
  | 'ALPHABETICAL_INDEX_COMPACT';

/** Indentation style. */
export enum Indentation {
  /** Sub-headings indented under parents (default) */
  NESTED = 'NESTED',
  /** All items at the same level */
  FLAT = 'FLAT',
}

/** @enumType */
export type IndentationWithLiterals = Indentation | 'NESTED' | 'FLAT';

/** Data for a smart block node. */
export interface SmartBlockData {
  /** The type of the smart block. */
  type?: SmartBlockDataTypeWithLiterals;
  /** Layout orientation. HORIZONTAL or VERTICAL. Optional for variants with fixed orientation. */
  orientation?: string | null;
  /** Column size controlling cells per row. */
  columnSize?: ColumnSizeWithLiterals;
  /**
   * Border color (for SOLID_JOINED_BOXES variant).
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Border width in pixels (for SOLID_JOINED_BOXES variant). */
  borderWidth?: number | null;
  /** Border radius in pixels (for SOLID_JOINED_BOXES variant). */
  borderRadius?: number | null;
  /** Node animation. */
  animation?: Animation;
}

/** Layout type of the smart block */
export enum SmartBlockDataType {
  /** Grid-based layouts with solid box items containing title, body, and icon/image. */
  SOLID_BOXES = 'SOLID_BOXES',
  /** Numbered boxes. */
  NUMBERED_BOXES = 'NUMBERED_BOXES',
  /** Statistics display with large numbers/values. */
  STATS = 'STATS',
  /** Statistics with circular visual elements. */
  CIRCLE_STATS = 'CIRCLE_STATS',
  /** Staggered/zigzag grid layout with alternating box positions. */
  SOLID_BOXES_ALTERNATING = 'SOLID_BOXES_ALTERNATING',
  /** Grid layout with boxes visually joined (no gaps, shared container border). */
  SOLID_JOINED_BOXES = 'SOLID_JOINED_BOXES',
  /** Transparent cells with only a left side line. */
  SIDE_LINE_TEXT = 'SIDE_LINE_TEXT',
  /** Transparent cells with only a top line. */
  TOP_LINE_TEXT = 'TOP_LINE_TEXT',
  /** Outlined boxes with a numbered/icon circle at the top. */
  OUTLINE_BOXES_WITH_TOP_CIRCLE = 'OUTLINE_BOXES_WITH_TOP_CIRCLE',
  /** Large icon bullets with text content. */
  BIG_BULLETS = 'BIG_BULLETS',
  /** Small dot bullets with text content. */
  SMALL_BULLETS = 'SMALL_BULLETS',
  /** Arrow icon bullets with text content. */
  ARROW_BULLETS = 'ARROW_BULLETS',
  /** Process steps with numbered/icon labels above a horizontal line. */
  PROCESS_STEPS = 'PROCESS_STEPS',
  /** Statistics with bar visual elements. */
  BAR_STATS = 'BAR_STATS',
  /** Timeline layout with numbered chips on a connecting line; cells alternate around the line. */
  TIMELINE = 'TIMELINE',
  /** Timeline layout with plain dot indicators; no numbers or shapes; cells alternate around the line. */
  MINIMAL_TIMELINE = 'MINIMAL_TIMELINE',
  /** Numbered pill-shaped labels (stadium chips) with text content; supports HORIZONTAL (pill on top) and VERTICAL (pill on left) orientations. */
  PILLS = 'PILLS',
  /** Star rating display with stars and a numeric value per cell. */
  STAR_RATING = 'STAR_RATING',
  /** Outlined boxes with decorative quote glyphs at the top-left and bottom-right corners. */
  QUOTE_BOXES = 'QUOTE_BOXES',
  /** Donut/ring with numbered annular-sector segments; cell text labels sit outside the ring. */
  CIRCLE = 'CIRCLE',
  /** Hierarchical pyramid where each cell renders as a horizontal slice (apex at top, base at bottom) with the cell number or shape centered inside; text content sits beside (desktop) or below (mobile) the pyramid. */
  PYRAMID = 'PYRAMID',
  /** Cells render a horizontal bar whose width scales with cell index, creating a staircase pattern; bar carries the cell number or a custom shape. */
  STAIRCASE = 'STAIRCASE',
  /** Hierarchical funnel where each cell renders as a horizontal slice (wide at top, narrowing toward the bottom) with the cell number or shape centered inside; text content sits beside (desktop) or below (mobile) the funnel. */
  VERTICAL_FUNNEL = 'VERTICAL_FUNNEL',
}

/** @enumType */
export type SmartBlockDataTypeWithLiterals =
  | SmartBlockDataType
  | 'SOLID_BOXES'
  | 'NUMBERED_BOXES'
  | 'STATS'
  | 'CIRCLE_STATS'
  | 'SOLID_BOXES_ALTERNATING'
  | 'SOLID_JOINED_BOXES'
  | 'SIDE_LINE_TEXT'
  | 'TOP_LINE_TEXT'
  | 'OUTLINE_BOXES_WITH_TOP_CIRCLE'
  | 'BIG_BULLETS'
  | 'SMALL_BULLETS'
  | 'ARROW_BULLETS'
  | 'PROCESS_STEPS'
  | 'BAR_STATS'
  | 'TIMELINE'
  | 'MINIMAL_TIMELINE'
  | 'PILLS'
  | 'STAR_RATING'
  | 'QUOTE_BOXES'
  | 'CIRCLE'
  | 'PYRAMID'
  | 'STAIRCASE'
  | 'VERTICAL_FUNNEL';

/** Column size controlling how many cells appear per row. */
export enum ColumnSize {
  /** Up to 4 cells in a row. */
  SMALL = 'SMALL',
  /** Up to 3 cells in a row (default). */
  MEDIUM = 'MEDIUM',
  /** Up to 2 cells in a row. */
  LARGE = 'LARGE',
  /** 1 cell in a row. */
  EXTRA_LARGE = 'EXTRA_LARGE',
}

/** @enumType */
export type ColumnSizeWithLiterals =
  | ColumnSize
  | 'SMALL'
  | 'MEDIUM'
  | 'LARGE'
  | 'EXTRA_LARGE';

/** Data for a smart block cell node. */
export interface SmartBlockCellData {
  /** Optional label text for the cell (e.g., for stats variants). */
  label?: string | null;
  /** Shape file details. */
  shape?: V1Media;
  /**
   * Border color of the cell.
   * @maxLength 19
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /** The type of the parent smart block (must match parent). */
  type?: SmartBlockDataTypeWithLiterals;
  /**
   * Accent color for non-background variants (e.g., line, bullet, label color).
   * @maxLength 19
   */
  accentColor?: string | null;
  /**
   * Background color for background-based variants (SOLID_BOXES, SOLID_BOXES_ALTERNATING, SOLID_JOINED_BOXES).
   * @maxLength 19
   */
  backgroundColor?: string | null;
  /**
   * Shape fill color as a hexadecimal value.
   * @maxLength 19
   */
  shapeColor?: string | null;
}

export interface CheckboxListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** Node animation. */
  animation?: Animation;
}

export interface ListItemNodeData {
  /** Checkbox list item state. Defaults to `false`. */
  checked?: boolean | null;
  /** Node animation. */
  animation?: Animation;
}

export interface Metadata {
  /** Schema version. */
  version?: number;
  /**
   * When the object was created.
   * @readonly
   * @deprecated
   */
  createdTimestamp?: Date | null;
  /**
   * When the object was most recently updated.
   * @deprecated
   */
  updatedTimestamp?: Date | null;
  /** Object ID. */
  _id?: string | null;
}

export interface DocumentStyle {
  /** Styling for H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Styling for H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Styling for H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Styling for H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Styling for H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Styling for H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Styling for paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Styling for block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Styling for code block nodes. */
  codeBlock?: TextNodeStyle;
}

export interface TextNodeStyle {
  /** The decorations to apply to the node. */
  decorations?: Decoration[];
  /** Padding and background color for the node. */
  nodeStyle?: NodeStyle;
  /** Line height for text in the node. */
  lineHeight?: string | null;
}

export interface Media {
  /**
   * Main media (image, video, etc.) associated with this product.
   * Automatically set to the first item in the media list.
   * @readonly
   */
  main?: ProductMedia;
  /**
   * All media items.
   * > **Note:** Returned only when you pass `"MEDIA_ITEMS_INFO"` to the `fields` array in Products API requests.
   */
  itemsInfo?: MediaItemsInfo;
}

export interface ProductMedia
  extends ProductMediaSetByOneOf,
    ProductMediaMediaOneOf {
  /**
   * Set media by ID of an existing file in Wix Media Manager.
   * @minLength 1
   * @maxLength 200
   */
  _id?: string;
  /**
   * Set media using an external media URL.
   * @format WEB_URL
   */
  url?: string;
  /**
   * Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field.
   * @readonly
   */
  image?: string;
  /**
   * Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field.
   * @readonly
   */
  video?: string;
  /**
   * Image alt text.
   * @minLength 1
   * @maxLength 1000
   */
  altText?: string | null;
  /**
   * Media display name.
   *
   * Overrides the default media name. Can be passed only when the media is set by a URL in this item's `url` field.
   * @maxLength 80
   */
  displayName?: string | null;
  /**
   * Media type.
   * @readonly
   */
  mediaType?: MediaTypeWithLiterals;
  /**
   * Media thumbnail.
   * > **Note:** Returned only when you pass `"THUMBNAIL"` to the `fields` array in Products API requests.
   */
  thumbnail?: Thumbnail;
  /**
   * ID used to upload media to Wix Media Manager.
   * @readonly
   * @format GUID
   */
  uploadId?: string;
}

/** @oneof */
export interface ProductMediaSetByOneOf {
  /**
   * Set media by ID of an existing file in Wix Media Manager.
   * @minLength 1
   * @maxLength 200
   */
  _id?: string;
  /**
   * Set media using an external media URL.
   * @format WEB_URL
   */
  url?: string;
}

/** @oneof */
export interface ProductMediaMediaOneOf {
  /**
   * Image details. Populated when you provide a [Wix Media Manager](https://support.wix.com/en/article/wix-media-about-the-media-manager) file ID in this item's `id` field.
   * @readonly
   */
  image?: string;
  /**
   * Video details. Populated when you provide a Wix Media Manager file ID in this istem's `id` field.
   * @readonly
   */
  video?: string;
}

export interface VideoResolution {
  /**
   * Video URL.
   * @format WEB_URL
   */
  url?: string;
  /** Video height. */
  height?: number;
  /** Video width. */
  width?: number;
  /**
   * Deprecated. Use the `posters` property in the parent entity instead.
   * @internal
   * @deprecated Deprecated. Use the `posters` property in the parent entity instead.
   */
  poster?: string;
  /**
   * Video format for example, mp4, hls.
   * @maxLength 200
   */
  format?: string;
  /**
   * Deprecated. Use the `urlExpirationDate` property in the parent entity instead.
   * @internal
   * @deprecated Deprecated. Use the `urlExpirationDate` property in the parent entity instead.
   * @replacedBy VideoV2.url_expiration_date
   */
  urlExpirationDate?: Date | null;
  /**
   * Deprecated. Use the `sizeInBytes` property in the parent entity instead. Size cannot be provided per resolution.
   * @internal
   * @format DECIMAL_VALUE
   * @deprecated Deprecated. Use the `sizeInBytes` property in the parent entity instead. Size cannot be provided per resolution.
   * @replacedBy VideoV2.size_in_bytes
   */
  sizeInBytes?: string | null;
  /**
   * Video quality. For example: 480p, 720p.
   * @internal
   * @maxLength 200
   */
  quality?: string | null;
  /**
   * Video filename.
   * @internal
   * @maxLength 200
   */
  filename?: string | null;
  /**
   * Video duration in seconds.
   * @internal
   * @readonly
   * @deprecated Video duration in seconds.
   * @replacedBy duration_in_milliseconds
   */
  durationInSeconds?: number | null;
  /**
   * Video duration in milliseconds.
   * @internal
   * @readonly
   */
  durationInMilliseconds?: number | null;
  /**
   * When true, this is a protected asset, and calling the URL will return a 403 error.
   * In order to access private assets, make a request to:
   * `GenerateFileDownloadUrl` with the WixMedia id and specify the asset_key in the request
   * @internal
   * @readonly
   */
  private?: boolean | null;
  /**
   * Key to identify the video resolution's relationship to the original media in WixMedia.
   * Can be used to request a download for the specific video resolution.
   * For example: 480p.mp4, 720p.mp4, 1080p.mp4, trailer-720p.mp4, clip-720p.mp4
   * @internal
   * @readonly
   * @maxLength 200
   */
  assetKey?: string | null;
}

export enum MediaType {
  /** Image media type. */
  IMAGE = 'IMAGE',
  /** Video media type. */
  VIDEO = 'VIDEO',
}

/** @enumType */
export type MediaTypeWithLiterals = MediaType | 'IMAGE' | 'VIDEO';

export interface Thumbnail {
  /**
   * Thumbnail url.
   * @format WEB_URL
   */
  url?: string;
  /** Thumbnail height. */
  height?: number;
  /** Thumbnail width. */
  width?: number;
  /**
   * Thumbnail alt text.
   * @minLength 1
   * @maxLength 1000
   */
  altText?: string | null;
}

export interface MediaItemsInfo {
  /**
   * All media items associated with this product.
   *
   * The first item in the array is automatically set as the product's main media.
   * @maxSize 50
   */
  items?: ProductMedia[];
}

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   *
   * Which of these an API actually accepts depends on where the tag is being set: this
   * list is not a blanket guarantee across every API that reuses this message. Site SEO
   * Tags in particular accepts only `meta` (see `SiteSeoTags.tags`) and rejects
   * `title`/`script`/`link` with a validation error even though they appear here —
   * check the field description of the specific `tags`-typed field you're writing to
   * for the types it actually supports and the error it returns.
   *
   * Limitation: `title` and `script` (e.g. a `script` tag with `props.type`
   * `"application/ld+json"` carrying structured data) are supported only when
   * `custom` is unset or `false`. Setting `custom: true` on a `title` or `script`
   * tag is rejected with `TAG_TYPE_NOT_ALLOWED`, because `custom: true` routes the
   * write through the site's Advanced/Custom Tags list. To write structured data,
   * omit `custom` (or set it to `false`).
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /**
   * Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages) —
   * i.e. an entry from the site's Advanced/Custom Tags list.
   *
   * Setting this to `true` on a write restricts which `type` values are accepted
   * — the same restriction the Advanced/Custom Tags UI enforces. `title` and
   * `script` tags (including JSON-LD structured data) must be sent with `custom`
   * omitted or `false`; sending them with `custom: true` is rejected with
   * `TAG_TYPE_NOT_ALLOWED`.
   *
   * Note this is a separate restriction from Site SEO Tags' own type restriction on
   * `SiteSeoTags.tags`: even with `custom` unset, Site SEO Tags rejects `script` (e.g.
   * for site-wide structured data) unconditionally. There is currently no way to set
   * site-wide, every-page structured data through any of these APIs — write a per-page
   * `script` tag through the Item SEO Tags API instead.
   *
   * This flag is scoped to that feature only. It is NOT an indicator of whether a standard tag
   * (`title`, `description`, Open Graph, etc.) reflects a manually configured value versus a computed
   * default/pattern — standard tags always resolve with `custom: false`, even when their content comes
   * from a page's manually saved SEO title/description override. Don't use this field to verify whether
   * a live page reflects a manual per-page SEO edit.
   *
   * Limitation: when `ResolveStaticPageSeoTags` is called without `seoData`, the per-page SEO data is read
   * only from the Vibe/Wix-managed-headless override store. That store holds nothing for classic Wix Editor
   * or Wix Studio pages, so even genuine Advanced/Custom Tags saved on those pages are missing from the
   * response entirely — there, `false` means this API could not tell, not that no custom tag exists.
   * Pass `seoData` explicitly to resolve against a known set of tags.
   */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface ConnectedOption extends ConnectedOptionOptionSettingsOneOf {
  /** Choices settings. */
  choicesSettings?: ChoicesSettings;
  /**
   * ID of a customization with `customizationType: PRODUCT_OPTION`.
   * @minLength 1
   * @maxLength 36
   */
  _id?: string | null;
  /**
   * Option name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string | null;
  /** Option render type. */
  optionRenderType?: ProductOptionRenderTypeWithLiterals;
  /**
   * A read-only identifier generated from the option name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string;
}

/** @oneof */
export interface ConnectedOptionOptionSettingsOneOf {
  /** Choices settings. */
  choicesSettings?: ChoicesSettings;
}

export enum ProductOptionRenderType {
  /** Text choices. */
  TEXT_CHOICES = 'TEXT_CHOICES',
  /** Swatch choices. */
  SWATCH_CHOICES = 'SWATCH_CHOICES',
}

/** @enumType */
export type ProductOptionRenderTypeWithLiterals =
  | ProductOptionRenderType
  | 'TEXT_CHOICES'
  | 'SWATCH_CHOICES';

export interface ChoicesSettings {
  /**
   * List of available choices for the option.
   * @minSize 1
   * @maxSize 1000
   * @immutable
   */
  choices?: ConnectedOptionChoice[];
}

export interface ConnectedOptionChoice extends ConnectedOptionChoiceValueOneOf {
  /**
   * Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.
   * @minLength 3
   * @maxLength 20
   */
  colorCode?: string;
  /**
   * Multiple colors  - HEX (#RRGGBB) color code
   * @internal
   */
  colorCodes?: MultipleColors;
  /**
   * Image
   * @internal
   */
  image?: string;
  /**
   * Choice ID.
   * @format GUID
   */
  choiceId?: string | null;
  /**
   * Product media to display when this choice is selected.
   *
   * When not empty, only these images are shown when a customer selects this choice. Otherwise, all product images are shown.
   * When multiple choices from different options are selected, only media present in `linkedMedia` of ALL selected choices are shown.
   * For example, if `Color: Red` has images 1, 2, 3 and `Material: Silk` has images 2, 3, 5, then only images 2 and 3 are shown when both are selected.
   * @maxSize 15
   * @deprecated Product media to display when this choice is selected.
   *
   * When not empty, only these images are shown when a customer selects this choice. Otherwise, all product images are shown.
   * When multiple choices from different options are selected, only media present in `linkedMedia` of ALL selected choices are shown.
   * For example, if `Color: Red` has images 1, 2, 3 and `Material: Silk` has images 2, 3, 5, then only images 2 and 3 are shown when both are selected.
   * @replacedBy media
   * @targetRemovalDate 2026-12-31
   */
  linkedMedia?: ProductMedia[];
  /** Choice type. */
  choiceType?: ChoiceTypeWithLiterals;
  /**
   * A read-only identifier generated from the choice name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string;
  /**
   * Choice name. For text choices, this field also contains the choice value.
   * @minLength 1
   * @maxLength 50
   */
  name?: string | null;
  /**
   * Whether at least one variant with this choice is in stock in the default location of the store.
   *
   * For example, a product with Color and Size options has these variants: [Blue, Small] which is out of stock and [Red, Large] which is in stock. For choice Blue, this field is `false`. For choice Red, this field is `true`.
   * @readonly
   */
  inStock?: boolean;
  /**
   * Whether at least one variant with this choice is visible.
   *
   * Default: `false`
   * @readonly
   */
  visible?: boolean;
  /**
   * Image to display alongside the choice.
   *
   * This field is returned only when the `PRODUCT_CHOICES_DISPLAY_IMAGE` requested field is included in the `fields` request parameter.
   * The returned image contains only its ID, URL, and alt text.
   */
  displayImage?: string;
  /**
   * Media references for this choice.
   *
   * On *write*, you can use either `media_id` or `url` to assign a media to this choice from the product's media.
   * On *read*, this field will return the media IDs of the associated media items.
   * When this field is requested via the `PRODUCT_CHOICES_MEDIA_REFERENCES` field, the `linked_media` field will be returned empty to avoid duplication.
   *
   * > **Note:** Use this field instead of `linked_media`. To use it in your read flow, pass `"PRODUCT_CHOICES_MEDIA_REFERENCES"` to the `fields` array in your request.
   */
  media?: MediaReferences;
}

/** @oneof */
export interface ConnectedOptionChoiceValueOneOf {
  /**
   * Single color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color). For example, `#FF0000` for red.
   * @minLength 3
   * @maxLength 20
   */
  colorCode?: string;
  /**
   * Multiple colors  - HEX (#RRGGBB) color code
   * @internal
   */
  colorCodes?: MultipleColors;
  /**
   * Image
   * @internal
   */
  image?: string;
}

export enum ChoiceType {
  /** Text choice. */
  CHOICE_TEXT = 'CHOICE_TEXT',
  /** Single color choice. */
  ONE_COLOR = 'ONE_COLOR',
  /**
   * Multiple colors choice.
   * @internal
   */
  MULTIPLE_COLORS = 'MULTIPLE_COLORS',
  /**
   * Image choice.
   * @internal
   */
  IMAGE = 'IMAGE',
}

/** @enumType */
export type ChoiceTypeWithLiterals = ChoiceType | 'CHOICE_TEXT' | 'ONE_COLOR';

export interface MultipleColors {
  /**
   * List of color codes.
   * @minLength 3
   * @maxLength 20
   * @minSize 2
   * @maxSize 2
   */
  colorCodes?: string[];
}

/** A list of references to product media items. */
export interface MediaReferences {
  /**
   * References to media items from the product's media list.
   * Each reference points to a media item by its ID or URL.
   * @maxSize 15
   */
  items?: MediaReference[];
}

/**
 * A reference to a product media item.
 * When writing, you can use either `media_id` or `url` to assign an item from the product's media.
 * When reading, this message will return the media ID of the associated media item.
 */
export interface MediaReference extends MediaReferenceIdentifierOneOf {
  /**
   * Media ID from the Wix Media Manager. Will always be returned on read.
   * @minLength 1
   * @maxLength 200
   */
  mediaId?: string;
  /**
   * External media URL. Can be used only on write.
   * @format WEB_URL
   */
  url?: string;
}

/** @oneof */
export interface MediaReferenceIdentifierOneOf {
  /**
   * Media ID from the Wix Media Manager. Will always be returned on read.
   * @minLength 1
   * @maxLength 200
   */
  mediaId?: string;
  /**
   * External media URL. Can be used only on write.
   * @format WEB_URL
   */
  url?: string;
}

export interface ConnectedModifier
  extends ConnectedModifierModifierSettingsOneOf {
  /** Free text modifier settings. */
  freeTextSettings?: FreeTextSettings;
  /** Choice settings. */
  choicesSettings?: ModifierChoicesSettings;
  /**
   * ID of a customization with `customizationType: MODIFIER`.
   * @minLength 1
   * @maxLength 36
   */
  _id?: string | null;
  /**
   * Modifier title.
   * @minLength 1
   * @maxLength 50
   */
  name?: string | null;
  /** Modifier render type. */
  modifierRenderType?: ModifierRenderTypeWithLiterals;
  /** Whether customer input is required for this modifier. */
  mandatory?: boolean;
  /**
   * A read-only identifier generated from the modifier name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string;
}

/** @oneof */
export interface ConnectedModifierModifierSettingsOneOf {
  /** Free text modifier settings. */
  freeTextSettings?: FreeTextSettings;
  /** Choice settings. */
  choicesSettings?: ModifierChoicesSettings;
}

export enum ModifierRenderType {
  /** Free text. */
  FREE_TEXT = 'FREE_TEXT',
  /** Text choices. */
  TEXT_CHOICES = 'TEXT_CHOICES',
  /** Swatch choices. */
  SWATCH_CHOICES = 'SWATCH_CHOICES',
}

/** @enumType */
export type ModifierRenderTypeWithLiterals =
  | ModifierRenderType
  | 'FREE_TEXT'
  | 'TEXT_CHOICES'
  | 'SWATCH_CHOICES';

export interface FreeTextSettings {
  /** Minimum number of characters. */
  minCharCount?: number;
  /**
   * Maximum number of characters.
   * @max 500
   */
  maxCharCount?: number;
  /**
   * Default amount to be added to the product's price.
   * @decimalValue options { gte:0, lte:999999999.99, maxScale:3 }
   */
  defaultAddedPrice?: string | null;
  /**
   * Title of the text to be input by the customer.
   * @minLength 1
   * @maxLength 150
   */
  title?: string;
  /**
   * A read-only identifier generated from the title.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 150
   */
  key?: string;
}

export interface ModifierChoicesSettings {
  /**
   * List of modifier choices.
   * @minSize 1
   * @maxSize 1000
   * @immutable
   */
  choices?: ConnectedModifierChoice[];
}

export interface ConnectedModifierChoice
  extends ConnectedModifierChoiceValueOneOf {
  /**
   * Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).
   * @minLength 3
   * @maxLength 20
   */
  colorCode?: string;
  /**
   * Multiple colors  - HEX (#RRGGBB) color code
   * @internal
   */
  colorCodes?: MultipleColors;
  /**
   * Image
   * @internal
   */
  image?: string;
  /**
   * Choice ID.
   * @format GUID
   */
  choiceId?: string | null;
  /**
   * Product media.
   * @maxSize 15
   * @deprecated Product media.
   * @replacedBy media
   * @targetRemovalDate 2026-12-31
   */
  linkedMedia?: ProductMedia[];
  /** Choice type. */
  choiceType?: ChoiceTypeWithLiterals;
  /**
   * A read-only identifier generated from the choice name.
   *
   * Use `key` in the `catalogReference.options` object when [integrating Catalog V3 with eCommerce APIs](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/e-commerce-integration).
   * @readonly
   * @minLength 1
   * @maxLength 50
   */
  key?: string;
  /**
   * Choice name.
   * @minLength 1
   * @maxLength 50
   */
  name?: string | null;
  /**
   * Added price.
   * @decimalValue options { gte:0, lte:999999999.99, maxScale:3 }
   */
  addedPrice?: string | null;
  /**
   * Image to display alongside the choice.
   *
   * This field is returned only when the `PRODUCT_CHOICES_DISPLAY_IMAGE` requested field is included in the `fields` request parameter.
   * The returned image contains only its ID, URL, and alt text.
   */
  displayImage?: string;
  /**
   * Media references for this choice.
   *
   * On *write*, you can use either `media_id` or `url` to assign a media to this choice from the product's media.
   * On *read*, this field will return the media IDs of the associated media items.
   * When this field is requested via the `PRODUCT_CHOICES_MEDIA_REFERENCES` field, the `linked_media` field will be returned empty to avoid duplication.
   *
   * > **Note:** Use this field instead of `linked_media`. To use it in your read flow, pass `"PRODUCT_CHOICES_MEDIA_REFERENCES"` to the `fields` array in your request.
   */
  media?: MediaReferences;
}

/** @oneof */
export interface ConnectedModifierChoiceValueOneOf {
  /**
   * Color code in HEX format, [as described by MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/hex-color).
   * @minLength 3
   * @maxLength 20
   */
  colorCode?: string;
  /**
   * Multiple colors  - HEX (#RRGGBB) color code
   * @internal
   */
  colorCodes?: MultipleColors;
  /**
   * Image
   * @internal
   */
  image?: string;
}

export interface Brand {
  /**
   * Brand ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Brand name.
   * @maxLength 50
   */
  name?: string | null;
}

export interface InfoSection {
  /**
   * Info section ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Info section unique name.
   * > **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.
   * @maxLength 100
   */
  uniqueName?: string | null;
  /**
   * Info section title.
   * > **Note:** Returned only when you pass `"INFO_SECTION"` to the `fields` array in Products API requests.
   * @minLength 1
   * @maxLength 50
   * @readonly
   */
  title?: string | null;
  /**
   * Info section description using rich content.
   * > **Note:** Returned only when you pass `"INFO_SECTION_DESCRIPTION"` to the `fields` array in Products API requests.
   *
   * <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="indent.emoji.divider.codeBlock.file.gallery.giphy.image.table.link.textHighlight.textColor" exampleid="7dc9240e-d548-417a-abcf-0291b68b4303">
   * <a href="https://dev.wix.com/docs/ricos/api-reference/ricos-document">See Ricos document reference</a>
   * </widget>
   * @readonly
   */
  description?: RichContent;
  /**
   * Info section description in HTML.
   *
   * When provided on create/update, this string must be a valid HTML. It will then be converted to rich content.
   * `plainDescription` is ignored when value is also passed to the `description` field.
   * > **Note:** Returned only when you pass `"INFO_SECTION_PLAIN_DESCRIPTION"` to the `fields` array in Products API requests.
   * @maxLength 16000
   */
  plainDescription?: string | null;
}

export interface Ribbon {
  /**
   * Ribbon ID.
   * @minLength 1
   * @maxLength 36
   */
  _id?: string | null;
  /**
   * Ribbon name.
   * @maxLength 30
   */
  name?: string | null;
}

export enum ProductType {
  /** Physical product. */
  PHYSICAL = 'PHYSICAL',
  /** Digital product. */
  DIGITAL = 'DIGITAL',
}

/** @enumType */
export type ProductTypeWithLiterals = ProductType | 'PHYSICAL' | 'DIGITAL';

export interface PhysicalProperties {
  /**
   * Price per unit settings.
   * > **Note:** Use this field only when relevant. For example, for products that are sold by weight, volume, or length.
   */
  pricePerUnit?: PricePerUnitSettings;
  /**
   * Fulfiller ID.
   * @format GUID
   */
  fulfillerId?: string | null;
  /**
   * Shipping group ID.
   * @internal
   * @format GUID
   */
  shippingGroupId?: string | null;
  /**
   * Product shipping weight range - Calculated from the lightest and heaviest variant shipping weights.
   * @readonly
   */
  shippingWeightRange?: WeightRange;
  /**
   * Product price per unit range. The minimum and maximum price per unit of all the variants.
   * @readonly
   */
  pricePerUnitRange?: PricePerUnitRange;
  /**
   * Weight measurement unit.
   * > **Note:** Returned only when you pass `"WEIGHT_MEASUREMENT_UNIT_INFO"` to the `fields` array in Products API requests.
   * @readonly
   */
  weightMeasurementUnitInfo?: WeightMeasurementUnitInfo;
  /**
   * Delivery profile ID.
   * @format GUID
   */
  deliveryProfileId?: string | null;
}

export interface PricePerUnitSettings {
  /**
   * Quantity.
   * For example, to define price per per 100 grams, set this field to `100`.
   * @min 0.01
   * @max 999999999.99
   */
  quantity?: number;
  /**
   * Measurement unit.
   * For example, to define price per 100 grams, set this field to "G".
   */
  measurementUnit?: MeasurementUnitWithLiterals;
}

export enum MeasurementUnit {
  UNSPECIFIED = 'UNSPECIFIED',
  ML = 'ML',
  CL = 'CL',
  L = 'L',
  CBM = 'CBM',
  MG = 'MG',
  G = 'G',
  KG = 'KG',
  MM = 'MM',
  CM = 'CM',
  M = 'M',
  SQM = 'SQM',
  OZ = 'OZ',
  LB = 'LB',
  FLOZ = 'FLOZ',
  PT = 'PT',
  QT = 'QT',
  GAL = 'GAL',
  IN = 'IN',
  FT = 'FT',
  YD = 'YD',
  SQFT = 'SQFT',
}

/** @enumType */
export type MeasurementUnitWithLiterals =
  | MeasurementUnit
  | 'UNSPECIFIED'
  | 'ML'
  | 'CL'
  | 'L'
  | 'CBM'
  | 'MG'
  | 'G'
  | 'KG'
  | 'MM'
  | 'CM'
  | 'M'
  | 'SQM'
  | 'OZ'
  | 'LB'
  | 'FLOZ'
  | 'PT'
  | 'QT'
  | 'GAL'
  | 'IN'
  | 'FT'
  | 'YD'
  | 'SQFT';

export interface WeightRange {
  /**
   * Minimum weight across all variants associated with this product.
   * @max 999999999.99
   */
  minValue?: number;
  /**
   * Maximum weight across all variants associated with this product.
   * @max 999999999.99
   */
  maxValue?: number;
}

export interface PricePerUnitRange {
  /** Minimum price per unit across all variants. */
  minValue?: PricePerUnitRangePricePerUnit;
  /** Maximum price per unit across all variants. */
  maxValue?: PricePerUnitRangePricePerUnit;
}

export interface PricePerUnitRangePricePerUnit {
  /**
   * Calculated value of price per unit. Takes into account pricePerUnit settings of product and variants and price of variants.
   * For example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$).
   * @readonly
   * @decimalValue options { gte:0, lte:999999999.99, maxScale:3 }
   */
  value?: string;
  /**
   * Price per unit info in the format of variant specific data / product setting, for example €4.00 / 1 Kg.
   * > **Note:** This field is returned by the API only when you pass `fields: "CURRENCY"` in a request.
   * @readonly
   * @maxLength 100
   */
  description?: string | null;
}

export interface WeightMeasurementUnitInfo {
  /**
   * Weight measurement unit.
   * @readonly
   */
  weightMeasurementUnit?: WeightUnitWithLiterals;
}

export enum WeightUnit {
  /** Weight unit can't be classified due to an error. */
  UNSPECIFIED_WEIGHT_UNIT = 'UNSPECIFIED_WEIGHT_UNIT',
  /** Kilograms. */
  KG = 'KG',
  /** Pounds. */
  LB = 'LB',
}

/** @enumType */
export type WeightUnitWithLiterals =
  | WeightUnit
  | 'UNSPECIFIED_WEIGHT_UNIT'
  | 'KG'
  | 'LB';

export interface VariantsInfo {
  /**
   * List of related variants.
   * Partial update of variants is not supported so on update you must pass all data for all of them.
   * If you want to update existing variant you must provide `id`, otherwise new variant with different id will be created which might break some integrations that rely on variant ids.
   * @minSize 1
   * @maxSize 1000
   */
  variants?: VariantWithInventory[];
}

export interface VariantWithInventory
  extends VariantWithInventoryTypedPropertiesOneOf {
  /** Physical properties. Must be passed when `productType: PHYSICAL` */
  physicalProperties?: VariantPhysicalProperties;
  /**
   * Digital properties. Must be passed when `productType: DIGITAL`.
   *
   * To attach a file, upload it using [Generate File Upload URL](https://dev.wix.com/docs/api-reference/assets/media/media-manager/files/generate-file-upload-url) and pass the resulting
   * `file.id` from the upload response as `digitalProperties.digitalFile.id`.
   * A variant without `digitalFile` set cannot be sold.
   */
  digitalProperties?: VariantDigitalProperties;
  /**
   * Variant ID.
   * @format GUID
   * @immutable
   */
  _id?: string | null;
  /**
   * Whether the variant is visible to site visitors.
   *
   * Default: `true`
   */
  visible?: boolean | null;
  /**
   * Variant SKU (stock keeping unit).
   * @minLength 1
   * @maxLength 40
   */
  sku?: string | null;
  /**
   * Variant barcode.
   * @minLength 1
   * @maxLength 40
   */
  barcode?: string | null;
  /** Variant price. */
  price?: PriceInfo;
  /**
   * Variant revenue details.
   *
   * > **Note:** Returned only when the following conditions are met:
   * > + You pass `"MERCHANT_DATA"` to the `fields` array in Products API requests.
   * > + Your app has the required `SCOPE.STORES.PRODUCT_READ_ADMIN` permission scope.
   */
  revenueDetails?: RevenueDetails;
  /** Inventory item of the variant on the default location. */
  inventoryItem?: InventoryItemComposite;
  /**
   * List of choices.
   * @maxSize 6
   * @immutable
   */
  choices?: OptionChoiceReferences[];
}

/** @oneof */
export interface VariantWithInventoryTypedPropertiesOneOf {
  /** Physical properties. Must be passed when `productType: PHYSICAL` */
  physicalProperties?: VariantPhysicalProperties;
  /**
   * Digital properties. Must be passed when `productType: DIGITAL`.
   *
   * To attach a file, upload it using [Generate File Upload URL](https://dev.wix.com/docs/api-reference/assets/media/media-manager/files/generate-file-upload-url) and pass the resulting
   * `file.id` from the upload response as `digitalProperties.digitalFile.id`.
   * A variant without `digitalFile` set cannot be sold.
   */
  digitalProperties?: VariantDigitalProperties;
}

export interface PriceInfo {
  /** Variant's current selling price. Must be greater than or equal to 0. */
  actualPrice?: FixedMonetaryAmount;
  /** Original price before any discount, shown with a strikethrough in the UI of Wix sites. Only set this if a discount applies. When set, it should be higher than `actualPrice` to reflect savings. */
  compareAtPrice?: FixedMonetaryAmount;
  /**
   * Variant selling price after automatic discounts are applied to `actualPrice`.
   *
   * > **Notes:**
   * > + Returned only when you pass `"DISCOUNT_INFO"` in Products API requests.
   * > + On Query Products and Search Products, returned only on `variantSummary.minPriceVariant` when `MIN_PRICE_VARIANT` is also requested.
   * > + `formattedAmount` is returned only when you also pass `"CURRENCY"`.
   * @readonly
   */
  priceAfterDiscount?: FixedMonetaryAmount;
}

export interface FixedMonetaryAmount {
  /**
   * Monetary amount. For example, `"3.99"`, or `"-4.99"` for a negative amount.
   * @decimalValue options { gte:0, lte:999999999.99, maxScale:3 }
   */
  amount?: string;
  /**
   * Formatted monetary amount. For example, `"$3.99"`.
   * > **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.
   * @readonly
   * @maxLength 20
   */
  formattedAmount?: string | null;
}

export interface RevenueDetails {
  /** Item cost. */
  cost?: FixedMonetaryAmount;
  /**
   * Profit. Calculated by reducing `cost` from `discountedPrice`.
   * @readonly
   */
  profit?: FixedMonetaryAmount;
  /**
   * Profit Margin. Calculated by dividing `profit` by `discountedPrice`.
   * The result is rounded to 4 decimal places.
   * @readonly
   * @max 1
   */
  profitMargin?: number;
}

export interface VariantPhysicalProperties {
  /**
   * Variant shipping weight.
   * product.physicalProperties.shipping_weight_range values are taken from here.
   * @max 999999999.99
   */
  weight?: number | null;
  /**
   * Price per unit info, in order to show price per unit on the product page.
   * For example if one sells cheese and defines 100g here then we know that buying this variant buyer receives 100g of cheese.
   * But on product page price will be displayed for units defined on product level. See `pricePerUnit.value` to understand how it's calculated.
   */
  pricePerUnit?: PricePerUnit;
  /** Product dimensions (length, width, height of the physical product). */
  productDimensions?: Dimensions;
  /** Package dimensions (length, width, height of the shipping package). */
  packageDimensions?: Dimensions;
}

export interface PricePerUnit {
  /**
   * Price per unit data for this variant.
   * `measurementUnit` value must correspond to the measurement unit set on the product.
   */
  settings?: PricePerUnitSettings;
  /**
   * Calculated value of price per unit. Takes into account `pricePerUnit` settings of parent product, of this variant, and discounted price of variant.
   * For example if discounted price is 2$, product's price per unit setting is 1 Kg, variant price per unit setting is 0.5 Kg then this value is 4$ (means variant weight is 0.5 Kg and it costs 2$ but we want to show price per 1 Kg so we show 4$).
   * @readonly
   * @decimalValue options { gte:0, lte:999999999.99, maxScale:3 }
   */
  value?: string;
  /**
   * Price per unit description.
   * > **Note:** Returned only when you pass `"CURRENCY"` to the `fields` array in Products API requests.
   * @readonly
   * @maxLength 100
   */
  description?: string | null;
  /**
   * Price per unit calculated from the variant automatic discount price.
   *
   * > **Notes:**
   * > + Returned only when you pass `"DISCOUNT_INFO"` in Products API requests.
   * > + `formattedAmount` is returned only when you also pass `"CURRENCY"`.
   * @readonly
   */
  priceAfterDiscount?: FixedMonetaryAmount;
}

export interface Dimensions {
  /**
   * Length. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  length?: string | null;
  /**
   * Width. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  width?: string | null;
  /**
   * Height. Measurement unit is handled at the system level (metric/imperial).
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  height?: string | null;
  /** Unit of measurement for dimensions (length, width, height). */
  unit?: DimensionsUnitWithLiterals;
}

export enum DimensionsUnit {
  UNKNOWN_DIMENSIONS_UNIT = 'UNKNOWN_DIMENSIONS_UNIT',
  /** Millimeters. */
  MM = 'MM',
  /** Centimeters. */
  CM = 'CM',
  /** Meters. */
  M = 'M',
  /** Inches. */
  IN = 'IN',
  /** Feet. */
  FT = 'FT',
  /** Yards. */
  YD = 'YD',
}

/** @enumType */
export type DimensionsUnitWithLiterals =
  | DimensionsUnit
  | 'UNKNOWN_DIMENSIONS_UNIT'
  | 'MM'
  | 'CM'
  | 'M'
  | 'IN'
  | 'FT'
  | 'YD';

export interface VariantDigitalProperties {
  /**
   * Digital file which will be downloaded by the buyer after successful purchase.
   * Each variant can have a different digital file, allowing you to offer different content per variant.
   *
   * `digitalFile.id` is a Media Manager file ID — upload the file via [Generate File Upload URL](https://dev.wix.com/docs/api-reference/assets/media/media-manager/files/generate-file-upload-url) and use the `file.id` from the upload response.
   */
  digitalFile?: SecuredMedia;
}

export interface SecuredMedia {
  /**
   * Media ID in media manager.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /**
   * Original file name.
   * @minLength 1
   * @maxLength 1000
   * @readonly
   */
  fileName?: string;
  /**
   * Original file size.
   * @minLength 1
   * @maxLength 1000
   * @readonly
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:0 }
   */
  fileSize?: string | null;
  /**
   * File type.
   * @readonly
   */
  fileType?: FileTypeWithLiterals;
}

export enum FileType {
  /** Unspecified file type. */
  UNSPECIFIED = 'UNSPECIFIED',
  /** Secure picture file. */
  SECURE_PICTURE = 'SECURE_PICTURE',
  /** Secure video file. */
  SECURE_VIDEO = 'SECURE_VIDEO',
  /** Secure document file. */
  SECURE_DOCUMENT = 'SECURE_DOCUMENT',
  /** Secure music file. */
  SECURE_MUSIC = 'SECURE_MUSIC',
  /** Secure archive file. */
  SECURE_ARCHIVE = 'SECURE_ARCHIVE',
  /** Secure raw file. */
  SECURE_RAW = 'SECURE_RAW',
}

/** @enumType */
export type FileTypeWithLiterals =
  | FileType
  | 'UNSPECIFIED'
  | 'SECURE_PICTURE'
  | 'SECURE_VIDEO'
  | 'SECURE_DOCUMENT'
  | 'SECURE_MUSIC'
  | 'SECURE_ARCHIVE'
  | 'SECURE_RAW';

export interface InventoryItemComposite
  extends InventoryItemCompositeTrackingMethodOneOf {
  /**
   * Tracking method - in stock.
   *
   * When set to `true`, the item is available for sale without a quantity limit.
   */
  inStock?: boolean;
  /**
   * Tracking method - quantity left in inventory.
   *
   * Quantity can be negative when decrementing inventory for an order that has already been paid.
   * @min -99999
   * @max 99999
   */
  quantity?: number;
  /**
   * Inventory item ID.
   * If you want to update existing inventory item you must provide id,
   * otherwise new inventory item with different id will be created.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Inventory item revision.
   * @readonly
   */
  revision?: string | null;
  /** Item preorder info. */
  preorderInfo?: PreorderInfo;
}

/** @oneof */
export interface InventoryItemCompositeTrackingMethodOneOf {
  /**
   * Tracking method - in stock.
   *
   * When set to `true`, the item is available for sale without a quantity limit.
   */
  inStock?: boolean;
  /**
   * Tracking method - quantity left in inventory.
   *
   * Quantity can be negative when decrementing inventory for an order that has already been paid.
   * @min -99999
   * @max 99999
   */
  quantity?: number;
}

export interface PreorderInfo {
  /**
   * Whether preorder is enabled for this inventory item.
   *
   * > **Note:** Preorder can't be enabled for digital products or products with subscriptions.
   *
   * Default: `false`
   */
  enabled?: boolean | null;
  /**
   * Message displayed to customers when the item is out of stock and preorder is enabled.
   * @minLength 1
   * @maxLength 250
   */
  message?: string | null;
  /**
   * Maximum number of items that can be preordered after stock reaches zero.
   *
   * Supported only for inventory items with `trackQuantity = true`.
   *
   * Default: `100000`
   * @max 100000
   */
  limit?: number | null;
  /**
   * Number of times this item has been preordered.
   *
   * Supported only for inventory items with `trackQuantity = true`.
   * @readonly
   * @max 99999
   */
  counter?: number | null;
  /**
   * Remaining quantity available for preorder.
   *
   * Supported only for items with `trackQuantity` set to `true`.
   * @readonly
   */
  quantity?: number | null;
}

export interface OptionChoiceReferences {
  /** Option and choice IDs. */
  optionChoiceIds?: OptionChoiceIds;
  /** Option and choice names. */
  optionChoiceNames?: OptionChoiceNames;
}

export interface OptionChoiceIds {
  /**
   * Option ID.
   * @minLength 1
   * @maxLength 36
   * @immutable
   */
  optionId?: string;
  /**
   * Choice ID.
   * @format GUID
   * @immutable
   */
  choiceId?: string;
}

export interface OptionChoiceNames {
  /**
   * Option name.
   * @minLength 1
   */
  optionName?: string;
  /**
   * Choice name.
   * @minLength 1
   */
  choiceName?: string;
  /** Render type. */
  renderType?: ProductOptionRenderTypeWithLiterals;
}

export interface SubscriptionDetails {
  /**
   * Subscriptions.
   * @minSize 1
   * @maxSize 6
   */
  subscriptions?: Subscription[];
  /**
   * Whether to allow one-time purchases in addition to subscription-based purchases.
   *
   * Default: `false`
   */
  allowOneTimePurchases?: boolean | null;
}

export interface Subscription extends SubscriptionCyclesOneOf {
  /** Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`. */
  autoRenewal?: boolean;
  /**
   * Number of billing cycles before subscription ends.
   * @min 2
   * @max 999
   */
  billingCycles?: number;
  /**
   * Subscription ID.
   * @format GUID
   * @immutable
   */
  _id?: string | null;
  /**
   * Subscription title.
   * @minLength 1
   * @maxLength 20
   */
  title?: string;
  /**
   * Subscription description.
   * @maxLength 60
   */
  description?: string | null;
  /**
   * Whether the subscription is visible to site visitors.
   *
   * Default: `true`
   */
  visible?: boolean | null;
  /**
   * Frequency of recurring payment.
   * For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.
   */
  frequency?: SubscriptionFrequencyWithLiterals;
  /**
   * Interval of recurring payment. Default: `1`. For example, if `frequency: MONTH`, `billingCycles: 3` and `interval: 2`; payment will be made every 2 months for a total of 6 months.
   * @min 1
   * @max 50
   */
  interval?: number | null;
  /**
   * Discount info (optional).
   * For example, a $20 discount would be `amount: 20`, `type: AMOUNT`.
   */
  discount?: SubscriptionDiscount;
}

/** @oneof */
export interface SubscriptionCyclesOneOf {
  /** Whether subscription is renewed automatically at the end of each period. Cannot be `false`, instead set `billingCycles`. */
  autoRenewal?: boolean;
  /**
   * Number of billing cycles before subscription ends.
   * @min 2
   * @max 999
   */
  billingCycles?: number;
}

/** Frequency unit of recurring payment */
export enum SubscriptionFrequency {
  UNDEFINED = 'UNDEFINED',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

/** @enumType */
export type SubscriptionFrequencyWithLiterals =
  | SubscriptionFrequency
  | 'UNDEFINED'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface SubscriptionDiscount
  extends SubscriptionDiscountDiscountOneOf {
  /**
   * Fixed amount subtracted from the variant price when calculating subscription prices.
   * @decimalValue options { gt:0, lte:999999999.99, maxScale:3 }
   */
  amountOff?: string;
  /**
   * Percentage subtracted from the variant price when calculating subscription prices.
   * @min 0.1
   * @max 99.9
   */
  percentOff?: number;
  /** Discount type. */
  type?: DiscountTypeWithLiterals;
}

/** @oneof */
export interface SubscriptionDiscountDiscountOneOf {
  /**
   * Fixed amount subtracted from the variant price when calculating subscription prices.
   * @decimalValue options { gt:0, lte:999999999.99, maxScale:3 }
   */
  amountOff?: string;
  /**
   * Percentage subtracted from the variant price when calculating subscription prices.
   * @min 0.1
   * @max 99.9
   */
  percentOff?: number;
}

export enum DiscountType {
  /** Discount by a specific amount. */
  AMOUNT = 'AMOUNT',
  /** Discount by a percentage. */
  PERCENT = 'PERCENT',
}

/** @enumType */
export type DiscountTypeWithLiterals = DiscountType | 'AMOUNT' | 'PERCENT';

export interface PurchaseEligibility {
  userCriteria?: UserCriteriaWithLiterals;
}

export enum UserCriteria {
  /** Product is available only to members. */
  MEMBERS_ONLY = 'MEMBERS_ONLY',
}

/** @enumType */
export type UserCriteriaWithLiterals = UserCriteria | 'MEMBERS_ONLY';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be used as in this example:
 * message Foo {
 * option (.wix.api.decomposite_of) = "wix.commons.v2.tags.Foo";
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface ProductCategoryIdsInfo {
  /**
   * List of category IDs to set for the product
   * @maxSize 1000
   * @format GUID
   */
  categoryIds?: string[];
}

export interface AddProductsToImportResponse {
  /**
   * Action results.
   * @minSize 1
   * @maxSize 100
   */
  results?: AddProductResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface AddProductResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
}

export interface ItemMetadata {
  /**
   * The product `handle` as passed in the `AddProductsToImport` request.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface SubmitCatalogImportRequest {
  /**
   * Catalog Import ID.
   * @format GUID
   */
  catalogImportId: string;
}

export interface SubmitCatalogImportResponse {
  /**
   * Job ID for async operation tracking. Pass to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job) or [List Async Job Items](https://dev.wix.com/docs/api-reference/business-management/async-job/list-async-job-items) to retrieve details and metadata
   * @format GUID
   */
  jobId?: string;
  /** Total number of items that will be processed in this import */
  totalProductsCount?: number;
}

export interface CatalogImportAlreadySubmittedErrorData {
  /**
   * Catalog Import ID.
   * @format GUID
   */
  catalogImportId?: string;
  /**
   * Async Job ID.
   * @format GUID
   */
  asyncJobId?: string;
}

export interface QueryProductItemsInImportRequest {
  /** WQL expression. */
  query?: CursorQuery;
  /**
   * Catalog Import ID.
   * @format GUID
   */
  catalogImportId?: string;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryProductItemsInImportResponse {
  /** Product items that satisfy the provided query. */
  productItems?: ProductItem[];
  /** Paging metadata. Contains a cursor that can be used in the next query. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
  /**
   * Total number of items matching the filter.
   * Available only on the first page of *Search* results, not included in *Query* or *List* results.
   * If the Search results span multiple pages, the value of `total` will exceed the number of items returned on the first page.
   * @internal
   */
  total?: number | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type SubmitCatalogImportApplicationErrors = {
  code?: 'CATALOG_IMPORT_ALREADY_SUBMITTED';
  description?: string;
  data?: CatalogImportAlreadySubmittedErrorData;
};

/**
 * Creates a CatalogImport for upsert product operations.
 *
 * Returns `catalogImport.id` that can be used in subsequent AddProductsToImport and SubmitCatalogImport calls.
 * @param catalogImport - CatalogImport to be created.
 * @public
 * @documentationMaturity preview
 * @requiredField catalogImport
 * @permissionId stores:catalog:v3:catalog_import:create_catalog_import
 * @applicableIdentity APP
 * @returns Created CatalogImport.
 * @fqn wix.stores.catalog.catalog_import.v3.CatalogImports.CreateCatalogImport
 */
export async function createCatalogImport(
  catalogImport: CatalogImport
): Promise<CatalogImport> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    catalogImport: catalogImport,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3CatalogImport.createCatalogImport(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.catalogImport!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { catalogImport: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['catalogImport']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Add products in bulk to an existing catalog import session.
 *
 * Products are stored in temporary storage until SubmitCatalogImport is called.
 * Can be called multiple times for the same import session.
 *
 * > **Notes:**
 * > + Products are upserted based on the `handle` field
 * > + Updates don't require passing the current revision number and overwrites existing fields which are specified in `product`
 * > + Pass `category_ids` to update categories assignments, overwrites existing assignments
 * @param catalogImportId - Catalog Import ID.
 * @param products - List of product items to add to the import session.
 * @public
 * @documentationMaturity preview
 * @requiredField catalogImportId
 * @requiredField products
 * @requiredField products.product
 * @requiredField products.product.handle
 * @permissionId stores:catalog:v3:catalog_import:add_products_to_import
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.catalog_import.v3.CatalogImports.AddProductsToImport
 */
export async function addProductsToImport(
  catalogImportId: string,
  products: NonNullablePaths<ProductItem, `product` | `product.handle`, 1>[]
): Promise<
  NonNullablePaths<
    AddProductsToImportResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      catalogImportId: catalogImportId,
      products: products,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'products.product.media.main.image' },
          { path: 'products.product.media.itemsInfo.items.image' },
          { path: 'products.product.options.choicesSettings.choices.image' },
          {
            path: 'products.product.options.choicesSettings.choices.displayImage',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.image',
          },
          { path: 'products.product.modifiers.choicesSettings.choices.image' },
          {
            path: 'products.product.modifiers.choicesSettings.choices.displayImage',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.image',
          },
        ],
      },
      {
        transformFn: transformSDKVideoV2ToRESTVideoV2,
        paths: [
          { path: 'products.product.media.main.video' },
          { path: 'products.product.media.itemsInfo.items.video' },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video',
          },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixStoresCatalogV3CatalogImport.addProductsToImport(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { catalogImportId: '$[0]', products: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['catalogImportId', 'products']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Submits all products in the catalog import for processing.
 * Once called, no more items can be added to this import session.
 * @param catalogImportId - Catalog Import ID.
 * @public
 * @documentationMaturity preview
 * @requiredField catalogImportId
 * @permissionId stores:catalog:v3:catalog_import:submit_catalog_import
 * @applicableIdentity APP
 * @fqn wix.stores.catalog.catalog_import.v3.CatalogImports.SubmitCatalogImport
 */
export async function submitCatalogImport(catalogImportId: string): Promise<
  NonNullablePaths<
    SubmitCatalogImportResponse,
    `jobId` | `totalProductsCount`,
    0
  > & {
    __applicationErrorsType?: SubmitCatalogImportApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    catalogImportId: catalogImportId,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3CatalogImport.submitCatalogImport(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { catalogImportId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['catalogImportId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
